import { Repayment, Repayments } from '@/domain/repayments'
import { FileSource } from '@/domain/file'
import {
  isTerminalNotApplicable,
  isTerminalRequired,
} from '@/domain/repayment-validation'
import {
  REPAYMENT_DATA_SOURCE_OPTIONS,
  REPAYMENT_DESCRIPTION_OPTIONS,
} from '@/domain/repayment-options'
import { AuthService } from '@/service/auth.service'
import { QueryParamsService } from '@/service/query-params.service'
import { RepaymentsTableQueryService } from '@/service/repayments-table-query.service'
import { RepaymentBulkEditJob, RepaymentsService } from '@/service/repayments.service'
import { UploadedFileService } from '@/service/uploaded-file.service'
import { CommonModule } from '@angular/common'
import {
  AfterContentInit,
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import {
  ActivatedRoute,
  NavigationStart,
  Params,
  Router,
} from '@angular/router'
import { ConfirmationService, MessageService } from 'primeng/api'
import { BadgeModule } from 'primeng/badge'
import { ButtonModule } from 'primeng/button'
import { ConfirmPopupModule } from 'primeng/confirmpopup'
import { DatePickerModule } from 'primeng/datepicker'
import { DialogModule } from 'primeng/dialog'
import { DropdownModule } from 'primeng/dropdown'
import { EditorModule } from 'primeng/editor'
import { InputNumberModule } from 'primeng/inputnumber'
import { InputTextModule } from 'primeng/inputtext'
import { InputTextarea } from 'primeng/inputtextarea'
import { MultiSelectModule } from 'primeng/multiselect'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { SkeletonModule } from 'primeng/skeleton'
import { ColumnFilter, Table, TableModule } from 'primeng/table'
import { ToastModule } from 'primeng/toast'
import { TooltipModule } from 'primeng/tooltip'
import { Subject } from 'rxjs'
import { filter, takeUntil } from 'rxjs/operators'
import { APP_CONFIG } from '../../../config/environment'
import { parseEmbedPermissions } from '../../embed-permissions'
import { OperationState } from '../../shared/operation-state'
import { ThemeSwitcher } from '../theme-switcher/theme-switcher.component'

const DEFAULT_SORT_ORDER = 1
const EMBED_DEFAULT_SORT_ORDER = -1
const DEFAULT_SORT_FIELD = 'repayment_date'
const COMMENTS_TOOLTIP_THRESHOLD = 60
const EDIT_FENCE_PADDING_PX = 300
const RELATION_DROPDOWN_PANEL_WIDTH_PX = {
  terminal_id: 250,
  cash_advance_id: 150,
  merchant_id: 400,
} as const
type EditableField =
  | 'description'
  | 'data_source'
  | 'merchant_id'
  | 'terminal_id'
  | 'cash_advance_id'
  | 'repayment_date'
  | 'payment_amount'
  | 'x365_split'
type QuickFilterRelationKind = 'merchant' | 'terminal' | 'cashAdvance'
type TableFilterConstraint = {
  value: boolean
  matchMode: 'equals'
  operator: 'and'
}

type BulkEditValues = {
  repayment_date: Date | null
  description: string | null
  data_source: string | null
  x365_split: number | null
  comments: string | null
  terminal_id: string | null
  cash_advance_id: string | null
  merchant_id: string | null
}

@Component({
  selector: 'repayments-table',
  templateUrl: 'repayments-table.component.html',
  standalone: true,
  styleUrl: 'repayments-table.component.scss',
  imports: [
    CommonModule,
    TableModule,
    BadgeModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    FormsModule,
    EditorModule,
    DialogModule,
    DropdownModule,
    InputTextarea,
    MultiSelectModule,
    ProgressSpinnerModule,
    SkeletonModule,
    TooltipModule,
    ToastModule,
    ConfirmPopupModule,
    DatePickerModule,
    ThemeSwitcher,
  ],
  providers: [ConfirmationService, MessageService],
})
export class RepaymentsTableComponent
  implements OnInit, AfterContentInit, AfterViewInit, OnDestroy, OnChanges
{
  private static instanceCount = 0
  private static consumedAutoNewNavigationIds = new Set<number>()
  private readonly instanceId = ++RepaymentsTableComponent.instanceCount
  @Input()
  public repayments: Repayments = []

  @Input()
  sourceFileId!: string | null

  // The parent owns the authoritative file record for this view. The shared
  // uploaded-file list can lag behind this component during navigation.
  @Input()
  sourceFileStatus: string | null = null

  @Input()
  public quickFilter: boolean = false

  @Input()
  public quickFilterOpen: boolean = false

  @Input()
  set totalRepaymentsCount(value: number | null) {
    if (value === undefined) {
      return
    }
    this.totalRepaymentsForQuery =
      value !== null && !Number.isNaN(value) ? value : null
    this.updateHasMoreRepayments()
  }

  // The file header owns this count. Keep it separate from the table query
  // count, which can legitimately be only the current lazy-loaded page.
  private sourceFileRepaymentsCount: number | null = null

  @Input()
  set fileRepaymentsCount(value: number | string | null | undefined) {
    const count = Number(value)
    this.sourceFileRepaymentsCount =
      value !== null && value !== undefined && Number.isFinite(count)
        ? count
        : null
  }

  @Output()
  public quickFilterClicked: EventEmitter<void> = new EventEmitter<void>()

  @Output()
  public scrolledToBottom: EventEmitter<void> = new EventEmitter<void>()

  @Output()
  public pendingChangesChanged: EventEmitter<boolean> = new EventEmitter<boolean>()

  @Input()
  public dataTable!: Table

  @ViewChild('dataTable', { static: true })
  public dataTableChild!: Table

  @Input()
  merchantFilter: Array<{ name: string; id: string }> = new Array<{
    name: string
    id: string
  }>()
  @Input()
  terminalsFilter: Array<{ name: string; id: string }> = new Array<{
    name: string
    id: string
  }>()
  @Input()
  cashAdvanceFilter: Array<{ name: string; id: string }> = new Array<{
    name: string
    id: string
  }>()

  @Input()
  isInProgress: boolean = false

  @Input()
  canEdit: boolean = true

  @Input()
  canDelete: boolean = true

  @Input()
  canView: boolean = true

  // public dataTableComponent!: Table

  public selectedRepayments!: Repayments
  /**
   * Select All on a file is represented by its file ID, rather than by
   * downloading every repayment ID into the browser.
   */
  public allFileRepaymentsSelected = false
  private lastHeaderSelectionChangeAt = 0

  statuses!: any[]

  @Input()
  loading: boolean = false

  private internalLoading = false
  public readonly operationState = new OperationState()

  get isLoading(): boolean {
    return this.loading || this.internalLoading
  }

  isRepaymentActionPending(repayment: Repayment, action = 'save'): boolean {
    return this.operationState.isPending(
      `repayment:${this.getRepaymentKey(repayment)}:${action}`,
    )
  }

  isBulkActionPending(action: string): boolean {
    return this.operationState.isPending(`bulk:${action}`)
  }

  get selectedRepaymentsCount(): number {
    if (this.isWholeFileSelection) {
      return (
        this.sourceFileRepaymentsCount ??
        this.totalRepaymentsForQuery ??
        this.selectedRepayments?.length ??
        0
      )
    }
    return this.selectedRepayments?.length ?? 0
  }

  get hasSelectedRepayments(): boolean {
    return this.selectedRepaymentsCount > 0
  }

  /**
   * PrimeNG retains only the lazy-loaded page in [(selection)]. A checked
   * header has every row in that page selected, which is our fallback signal
   * when the library emits row events after its header event.
   */
  get isWholeFileSelection(): boolean {
    return !!this.sourceFileId && (
      this.allFileRepaymentsSelected ||
      (!!this.repayments?.length &&
        this.selectedRepayments?.length === this.repayments.length)
    )
  }

  onHeaderSelectionChange(event: Event): void {
    if (!this.sourceFileId) return
    // This event is emitted by the header input itself. Treat its checked
    // state as the file selection immediately; row events only describe the
    // currently rendered page and must not downgrade the selection to 100.
    const input = event.target as HTMLInputElement | null
    this.allFileRepaymentsSelected = input?.checked ?? !this.allFileRepaymentsSelected
    this.lastHeaderSelectionChangeAt = Date.now()
    if (!this.allFileRepaymentsSelected) this.selectedRepayments = []
  }

  onRowSelectionChanged(): void {
    // A manual row change turns the selection back into an explicit subset.
    // PrimeNG also emits page-row events just after the header event.
    if (Date.now() - this.lastHeaderSelectionChangeAt < 500) return
    this.allFileRepaymentsSelected = false
  }

  private repaymentActionKey(repayment: Repayment, action: string): string {
    return `repayment:${this.getRepaymentKey(repayment)}:${action}`
  }

  private get canReadRepayments(): boolean {
    return !this.destroyed && this.canView
  }

  sortField: string = DEFAULT_SORT_FIELD
  sortOrder: number = DEFAULT_SORT_ORDER

  activityValues: number[] = [0, 100]

  hasActiveFilters: boolean = false

  showDeletedOnly: boolean = false
  missingFieldsOnly: boolean = false

  public bulkEditDialogVisible = false
  public bulkEditValues: BulkEditValues = this.createEmptyBulkEditValues()
  public bulkEditJob: RepaymentBulkEditJob | null = null
  private bulkEditProgressPoll: ReturnType<typeof setInterval> | null = null

  public isEmbedded: boolean = false

  @HostBinding('class.embedded-table-host')
  get embeddedTableHost(): boolean {
    return this.isEmbedded
  }

  public embeddedPresetValues: {
    merchant_id?: string
    terminal_id?: string
    cash_advance_id?: string
  } = {}
  private pendingManualSaveRows = new Map<string, Repayment>()
  private pendingManualSaveOriginals = new Map<string, Repayment>()
  public readonly isCypress =
    typeof window !== 'undefined' && !!(window as any).Cypress

  private readonly destroy$ = new Subject<void>()
  private readonly lifecycleAbort = new AbortController()
  private activeQueryAbort: AbortController | null = null
  private editableTerminalScope = ''
  private editableCashAdvanceMerchantId = ''
  private relationDropdownSearchToken = 0
  private destroyed = false
  private scrollContainer?: HTMLElement
  private isAtBottom = false
  private highlightedColumnIndex: number | null = null
  private highlightedTableRoot: HTMLElement | null = null
  private readonly pageSize = 100
  public pendingQueryToken: symbol | null = null
  // Query construction is asynchronous because it resolves schema metadata.
  // Track intent from the instant a refresh starts so an older embed request
  // cannot become current after a newer, scoped refresh has started.
  private refreshGeneration = 0
  private readonly lastCallMap = new Map<string, number>()
  private lastAppliedQueryParams: Params | null = null
  private initialEmbedQueryParams: Params | null = null
  private suppressOnFilterRefresh = false
  public hasLoadedOnce = false
  public totalRepaymentsForQuery: number | null = null
  public hasMoreRepayments = false
  public merchantOptions: Array<{
    id: string
    name: string
    customerNumber?: string
  }> = []
  public terminalOptions: Array<{ id: string; name: string }> = []
  public editableTerminalOptions: Array<{ id: string; name: string }> = []
  public cashAdvanceOptions: Array<{
    id: string
    name: string
    activeCashAdvance?: boolean
  }> = []
  public editableCashAdvanceOptions: Array<{
    id: string
    name: string
    activeCashAdvance?: boolean
  }> = []
  public updatedByOptions: Array<{ label: string; value: string }> = []
  public readonly dataSourceOptions: Array<{ label: string; value: string }> =
    REPAYMENT_DATA_SOURCE_OPTIONS.map((value) => ({ label: value, value }))
  public readonly descriptionOptions: Array<{ label: string; value: string }> =
    REPAYMENT_DESCRIPTION_OPTIONS.map((value) => ({ label: value, value }))
  public commentsDialogVisible = false
  public commentsDialogValue = ''
  public activeOverflowTooltip: string | null = null
  public activeEditCell: {
    repaymentId: string
    field: EditableField
  } | null = null
  public editDraftValue: string | number | Date | null = null
  public shouldFlipActiveRelationDropdown = false
  private commentsOriginalValue = ''
  private commentsTargetRepayment: Repayment | null = null
  @ViewChild('commentsDialogInput')
  private commentsDialogInput?: ElementRef<HTMLTextAreaElement>
  @ViewChildren(ColumnFilter) columnFilters!: QueryList<ColumnFilter>
  public readonly dateLocale = {
    firstDayOfWeek: 1,
    dayNames: [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ],
    dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
    monthNames: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
    monthNamesShort: [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ],
    today: 'Today',
    clear: 'Clear',
  }

  private readonly standardColumns = [
    'repayment_date',
    'description',
    'previous_balance',
    'payment_amount',
    'balance',
    'data_source',
    'x365_split',
    'comments',
    'terminal_id',
    'cash_advance_id',
    'merchant_id',
    'created_date',
    'updated_by',
    'deleted',
  ]

  get columns(): string[] {
    return this.sourceFileId
      ? this.standardColumns.filter(
          (column) => column !== 'previous_balance' && column !== 'balance',
        )
      : this.standardColumns
  }

  /** Formats ledger posting timestamps in the browser's local time zone. */
  public formatCreatedDate(value: string | undefined): string {
    if (!value) {
      return ''
    }

    const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch
      return `${day}/${month}/${year}`
    }

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) {
      return ''
    }

    const pad = (number: number) => String(number).padStart(2, '0')
    const formattedDate = `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`

    // Historical date-only values were converted to midnight UTC by the
    // database migration. Midnight means no creation time is available.
    const isMidnightTimestamp = /[T\s]00:00:00(?:\.0+)?(?:Z|[+-]\d{2}:?\d{2})?$/i.test(
      value,
    )
    if (isMidnightTimestamp) {
      return formattedDate
    }

    return `${formattedDate} - ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  }

  constructor(
    private repaymentsService: RepaymentsService,
    private route: ActivatedRoute,
    private router: Router,
    private hostElement: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private authService: AuthService,
    private confirmationService: ConfirmationService,
    private readonly messageService: MessageService,
    private readonly queryParamsService: QueryParamsService,
    private readonly tableQueryService: RepaymentsTableQueryService,
    private readonly uploadedFileService: UploadedFileService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['repayments'] || changes['loading']) {
      const shouldUseInputLength =
        this.totalRepaymentsForQuery === null &&
        !this.isLoading &&
        !!this.sourceFileId

      if (shouldUseInputLength) {
        this.totalRepaymentsForQuery = this.repayments?.length ?? 0
      }
      if (changes['repayments'] && this.repayments) {
        this.repayments = this.withPendingManualSaves(
          this.normalizeRepaymentsForView(this.repayments),
        )
      }
      this.updateHasMoreRepayments()
    }

    if (changes['repayments']) {
      this.syncDropdownOptions()
    }
  }

  ngOnInit() {
    this.bulkEditProgressPoll = setInterval(() => void this.refreshBulkEditProgress(), 1000)
    void this.restoreActiveBulkEditJob()
    if (this.instanceId > 1) {
      console.warn(
        `[RepaymentsTableComponent] duplicate instance detected (${this.instanceId})`,
      )
    }
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.closeAllColumnFilters()
      })
    this.isEmbedded = this.route.snapshot.data['embedded'] === true
    if (this.isEmbedded) {
      this.sortOrder = EMBED_DEFAULT_SORT_ORDER
      this.tableQueryService.setRepaymentDateSecondarySorts([
        { field: 'embedded_balance_zero_first', order: 'asc' },
        { field: 'balance', order: 'asc' },
      ])
      this.initialEmbedQueryParams = this.cloneRouteParams(
        this.route.snapshot.queryParams || {},
      )
      this.applyPermissionsFromParams(this.route.snapshot.queryParams || {})
      const embeddedContext = this.authService.getEmbeddedUserContext()
      if (embeddedContext) {
        console.log('[embed][view] resolved embedded context', embeddedContext)
        console.log('[embed][view] resolved embedded role', {
          role: this.authService.getEmbeddedRole(),
        })
      }
    } else {
      this.tableQueryService.setRepaymentDateSecondarySorts([])
    }
    if (this.isInProgress && this.route.snapshot.queryParams['new'] === '1') {
      const navigationId =
        this.router.getCurrentNavigation()?.id ??
        (window.history.state?.navigationId as number | undefined)
      const shouldConsumeAutoNew =
        typeof navigationId !== 'number' ||
        !RepaymentsTableComponent.consumedAutoNewNavigationIds.has(navigationId)

      if (shouldConsumeAutoNew) {
        if (typeof navigationId === 'number') {
          RepaymentsTableComponent.consumedAutoNewNavigationIds.add(
            navigationId,
          )
          if (RepaymentsTableComponent.consumedAutoNewNavigationIds.size > 50) {
            RepaymentsTableComponent.consumedAutoNewNavigationIds.clear()
          }
        }

        this.router.navigate([], {
          queryParams: { new: null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        })
        setTimeout(() => this.addEmptyRow())
      }
    }
    this.tableQueryService.setSort(this.sortField, this.sortOrder)
    this.tableQueryService.setPagination(0, this.pageSize)
    this.applyDeletedRepaymentsFilter(false, this.dataTableChild, {
      applyTableFilter: false,
    })
    this.updateHasMoreRepayments()

    if (this.canReadRepayments) {
      this.applyRouteQueryParams(this.route.snapshot.queryParams || {})
    } else {
      this.clearRepaymentsForNoView()
    }

    this.queryParamsService.queryParams$
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        if (this.isEmbedded) {
          this.applyPermissionsFromParams(params)
        }
        if (!this.canReadRepayments) {
          this.clearRepaymentsForNoView()
          return
        }
        this.applyRouteQueryParams(params)
      })
    if (this.isEmbedded) {
      if (!this.canView) {
        this.clearRepaymentsForNoView()
        return
      }
      // Embed layout must not depend on the selected PrimeNG theme. The hidden
      // theme switcher can remove `material`, but the Canvas document itself
      // must remain a fixed viewport so this table owns the only scrollbar.
      document.body.classList.add('embedded')
      const embedParams = this.route.snapshot.queryParams || {}
      const hasEmbedFilters = Boolean(
        embedParams['identifierType'] ||
          embedParams['identifierValue'] ||
          embedParams['merchant_id'] ||
          embedParams['merchants(name)'] ||
          embedParams['terminal_id'] ||
          embedParams['terminals(name)'] ||
          embedParams['cash_advance_id'] ||
          embedParams['cash_advances(name)'] ||
          embedParams['updated_by'],
      )

      if (!hasEmbedFilters) {
        this.internalLoading = true
        this.repaymentsService
          .loadAllRepayments({ signal: this.lifecycleAbort.signal })
          .then(() => {
            if (this.destroyed) {
              return
            }
            this.repayments = this.withPendingManualSaves(
              this.normalizeRepaymentsForView(
                this.repaymentsService.repayments,
              ),
            )
            this.totalRepaymentsForQuery = this.repayments.length
            this.updateHasMoreRepayments()
            this.internalLoading = false
          })
          .catch((error) => this.swallowAbort(error))
      }
    }
  }

  private applyPermissionsFromParams(params: Record<string, unknown>): void {
    const permissions = parseEmbedPermissions(params)
    this.canView = permissions.canView
    this.canEdit = permissions.canEdit
    this.canDelete = permissions.canDelete

    if (!this.canView) {
      this.clearRepaymentsForNoView()
    }
  }

  private clearRepaymentsForNoView(): void {
    this.activeQueryAbort?.abort()
    this.activeQueryAbort = null
    this.internalLoading = false
    this.loading = false
    this.repayments = []
    this.totalRepaymentsForQuery = 0
    this.hasMoreRepayments = false
    this.selectedRepayments = []
  }

  private applyRouteQueryParams(
    params: Params,
    options: { force?: boolean; refresh?: boolean } = {},
  ): void {
    if (!this.canReadRepayments) {
      this.clearRepaymentsForNoView()
      return
    }

    const force = options.force ?? false
    const shouldRefresh = options.refresh ?? true

    if (
      this.destroyed ||
      (!force && this.areRouteParamsEqual(this.lastAppliedQueryParams, params))
    ) {
      return
    }

    this.lastAppliedQueryParams = this.cloneRouteParams(params)

    if (this.isEmbedded && APP_CONFIG.enableEmbeddedDebugLogging) {
      console.log('[embed][params] current query params', params)
    }
    const embeddedIdentifierType = this.isEmbedded
      ? (params['identifierType'] as string | undefined)
      : undefined
    const embeddedIdentifierValue = this.isEmbedded
      ? (params['identifierValue'] as string | undefined)
      : undefined
    const hasEmbeddedIdentifier =
      Boolean(embeddedIdentifierType) && Boolean(embeddedIdentifierValue)
    const embeddedIdentifierParam = hasEmbeddedIdentifier
      ? { type: embeddedIdentifierType, value: embeddedIdentifierValue }
      : null
    if (
      this.isEmbedded &&
      APP_CONFIG.enableEmbeddedDebugLogging &&
      embeddedIdentifierParam
    ) {
      console.log('[embed][params] identifier mapping', embeddedIdentifierParam)
    }

    const resolveIdentifierParam = (
      paramName: string,
      directParam: string | string[] | undefined,
    ): string | string[] | undefined => {
      if (!embeddedIdentifierParam) {
        return directParam
      }
      if (embeddedIdentifierParam.type === paramName) {
        return embeddedIdentifierParam.value
      }
      return directParam
    }

    const merchantIdParam = resolveIdentifierParam(
      'merchant_id',
      params['merchant_id'],
    )
    let quickFilterStateChanged = false

    if (merchantIdParam) {
      const merchantIds = Array.isArray(merchantIdParam)
        ? merchantIdParam
        : String(merchantIdParam).split(',')
      quickFilterStateChanged =
        this.filterTable(
          'merchant_id',
          merchantIds.map((id) => ({ name: String(id) })),
          { refresh: false, emitQuickFilter: false },
        ) || quickFilterStateChanged
    } else if (!Array.isArray(params['merchants(name)'])) {
      this.merchantFilter = params['merchants(name)']
        ?.split(',')
        ?.map((name: string) => ({
          name: name,
        }))
    } else {
      this.merchantFilter = params['merchants(name)']
    }

    const terminalIdParam = resolveIdentifierParam(
      'terminal_id',
      params['terminal_id'],
    )
    if (terminalIdParam) {
      const terminalIds = Array.isArray(terminalIdParam)
        ? terminalIdParam
        : String(terminalIdParam).split(',')
      quickFilterStateChanged =
        this.filterTable(
          'terminal_id',
          terminalIds.map((id) => ({ name: String(id) })),
          { refresh: false, emitQuickFilter: false },
        ) || quickFilterStateChanged
    } else if (!Array.isArray(params['terminals(name)'])) {
      this.terminalsFilter = params['terminals(name)']
        ?.split(',')
        ?.map((name: string) => ({
          name: name,
        }))
    } else {
      this.terminalsFilter = params['terminals(name)']
    }

    const cashAdvanceIdParam = resolveIdentifierParam(
      'cash_advance_id',
      params['cash_advance_id'],
    )
    if (cashAdvanceIdParam) {
      const cashAdvanceIds = Array.isArray(cashAdvanceIdParam)
        ? cashAdvanceIdParam
        : String(cashAdvanceIdParam).split(',')
      quickFilterStateChanged =
        this.filterTable(
          'cash_advance_id',
          cashAdvanceIds.map((id) => ({ name: String(id) })),
          { refresh: false, emitQuickFilter: false },
        ) || quickFilterStateChanged
    } else if (!Array.isArray(params['cash_advances(name)'])) {
      this.cashAdvanceFilter = params['cash_advances(name)']
        ?.split(',')
        ?.map((name: string) => ({
          name: name,
        }))
    } else {
      this.cashAdvanceFilter = params['cash_advances(name)']
    }

    if (!merchantIdParam) {
      quickFilterStateChanged =
        this.filterTable(
          'merchants(name)',
          this.merchantFilter?.map((m) => ({ name: m.name, id: m.id })),
          { refresh: false, emitQuickFilter: false },
        ) || quickFilterStateChanged
    }
    if (!terminalIdParam) {
      quickFilterStateChanged =
        this.filterTable(
          'terminals(name)',
          this.terminalsFilter?.map((m) => ({ name: m.name, id: m.id })),
          { refresh: false, emitQuickFilter: false },
        ) || quickFilterStateChanged
    }
    if (!cashAdvanceIdParam) {
      quickFilterStateChanged =
        this.filterTable(
          'cash_advances(name)',
          this.cashAdvanceFilter?.map((m) => ({ name: m.name, id: m.id })),
          { refresh: false, emitQuickFilter: false },
        ) || quickFilterStateChanged
    }

    if (quickFilterStateChanged && shouldRefresh) {
      void this.refreshTableData()
    }

    if (this.isEmbedded) {
      this.embeddedPresetValues = {
        merchant_id: this.resolveSingleEmbedId(merchantIdParam),
        terminal_id: this.resolveSingleEmbedId(terminalIdParam),
        cash_advance_id: this.resolveSingleEmbedId(cashAdvanceIdParam),
      }
    }
  }

  private cloneRouteParams(params: Params): Params {
    return Object.entries(params).reduce<Params>((clone, [key, value]) => {
      clone[key] = Array.isArray(value) ? [...value] : value
      return clone
    }, {})
  }

  private areRouteParamsEqual(
    previous: Params | null,
    current: Params,
  ): boolean {
    if (!previous) {
      return false
    }

    const previousKeys = Object.keys(previous)
    const currentKeys = Object.keys(current)
    if (previousKeys.length !== currentKeys.length) {
      return false
    }

    return previousKeys.every((key) => {
      const previousValue = previous[key]
      const currentValue = current[key]

      if (Array.isArray(previousValue) || Array.isArray(currentValue)) {
        if (!Array.isArray(previousValue) || !Array.isArray(currentValue)) {
          return false
        }
        if (previousValue.length !== currentValue.length) {
          return false
        }
        return previousValue.every(
          (value, index) => value === currentValue[index],
        )
      }

      return previousValue === currentValue
    })
  }

  ngAfterContentInit(): void {
    this.dataTable = this.dataTableChild
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.registerScrollListener()
    }, 1000)
  }

  @HostListener('mousemove', ['$event'])
  onHostMouseMove(event: MouseEvent): void {
    const target = event.target as HTMLElement | null
    if (!target) {
      this.clearColumnHoverEdge()
      return
    }

    const isResizer = !!target.closest('.p-datatable-column-resizer')
    if (!isResizer) {
      this.clearColumnHoverEdge()
      return
    }

    const tableRoot = target.closest('.p-datatable') as HTMLElement | null
    const headerCell = target.closest('th') as HTMLTableCellElement | null
    const isHeaderCell = !!headerCell?.closest('.p-datatable-thead')

    if (!tableRoot || !headerCell || !isHeaderCell) {
      this.clearColumnHoverEdge()
      return
    }

    this.applyColumnHoverEdge(tableRoot, headerCell.cellIndex + 1)
  }

  @HostListener('mouseleave')
  onHostMouseLeave(): void {
    this.clearColumnHoverEdge()
  }

  clear(
    table: Table,
    {
      refresh = true,
      syncRoute = true,
    }: { refresh?: boolean; syncRoute?: boolean } = {},
  ) {
    this.suppressOnFilterRefresh = true
    const defaultSortOrder = this.isEmbedded
      ? EMBED_DEFAULT_SORT_ORDER
      : DEFAULT_SORT_ORDER

    try {
      if (this.sortField !== DEFAULT_SORT_FIELD) {
        this.sortField = DEFAULT_SORT_FIELD
      }

      if (this.sortOrder !== defaultSortOrder) {
        this.sortOrder = defaultSortOrder
      }

      table.clearFilterValues()

      Object.keys(table.filters).forEach((key) => {
        table.filters[key] = [
          { matchMode: 'startsWith', operator: 'and', value: null },
        ]
      })

      this.applyDeletedRepaymentsFilter(false, table, {
        applyTableFilter: false,
        syncQueryFilters: false,
      })
      table._filter()
      this.sortField = DEFAULT_SORT_FIELD
      this.sortOrder = defaultSortOrder
      this.selectedRepayments = []
      this.allFileRepaymentsSelected = false

      const embedResetParams =
        this.isEmbedded && this.initialEmbedQueryParams
          ? this.cloneRouteParams(this.initialEmbedQueryParams)
          : null

      if (syncRoute) {
        const currentParams = embedResetParams ?? {
          ...this.route.snapshot.queryParams,
        }

        if (!embedResetParams) {
          delete currentParams['merchants(name)']
          delete currentParams['terminals(name)']
          delete currentParams['cash_advance(name)']
          delete currentParams['cash_advances(name)']
          delete currentParams['updated_by']

          // In embed mode, keep the identifying params so the host context
          // and filtered embed view can never be escaped by clearing filters.
          if (!this.isEmbedded) {
            delete currentParams['merchant_id']
            delete currentParams['identifierType']
            delete currentParams['identifierValue']
            delete currentParams['terminal_id']
            delete currentParams['cash_advance_id']
          }
        }

        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: currentParams,
          replaceUrl: true,
        })
      }
      this.tableQueryService.reset()
      this.missingFieldsOnly = false
      this.tableQueryService.setPagination(0, this.pageSize)
      this.tableQueryService.setSort(this.sortField, this.sortOrder)
      this.tableQueryService.setFilters(table.filters ?? {})

      if (embedResetParams) {
        this.applyRouteQueryParams(embedResetParams, {
          force: true,
          refresh: false,
        })
        this.tableQueryService.setFilters(table.filters ?? {})
      }
    } finally {
      this.suppressOnFilterRefresh = false
    }

    if (refresh) {
      void this.refreshTableData()
    }
  }

  onFilter(event: any) {
    if (this.suppressOnFilterRefresh) {
      return
    }

    const isFilterValuePresent = (value: unknown): boolean => {
      if (value === null || value === undefined) {
        return false
      }
      if (Array.isArray(value)) {
        return value.length > 0
      }
      if (typeof value === 'string') {
        return value.trim().length > 0
      }
      return true
    }

    this.hasActiveFilters = Object.values(event.filters).some(
      (filterMeta: any) => {
        const entries = Array.isArray(filterMeta)
          ? filterMeta
          : Array.isArray(filterMeta?.constraints)
            ? filterMeta.constraints
            : filterMeta
              ? [filterMeta]
              : []
        return entries.some(
          (f: any) =>
            isFilterValuePresent(f?.value) &&
            !(f?.matchMode === 'equals' && f?.value === false),
        )
      },
    )

    const updatedByFilter = event?.filters?.['updated_by']
    const updatedByEntries = Array.isArray(updatedByFilter)
      ? updatedByFilter
      : Array.isArray(updatedByFilter?.constraints)
        ? updatedByFilter.constraints
        : updatedByFilter
          ? [updatedByFilter]
          : []
    const hasUpdatedByFilter = updatedByEntries.some(
      (entry: { value?: unknown }) => isFilterValuePresent(entry?.value),
    )

    if (hasUpdatedByFilter && !this.repaymentsService.areUpdatedByCached()) {
      this.loadUpdatedByOptions()
    }

    this.tableQueryService.setFilters(event.filters ?? {})
    this.tableQueryService.resetPagination()
    this.refreshTableData()
  }

  onAmountFilterChange(
    value: number | string | null,
    filterCallback: (value: number | string | null) => void,
  ): void {
    if (this.destroyed) {
      return
    }
    filterCallback(value)
  }

  undoDelete(repayment: any) {
    if (this.destroyed || !this.canDelete) {
      return
    }
    const previousState = {
      deleted: !!repayment.deleted,
      staged: !!repayment.staged,
    }
    repayment.deleted = false
    repayment.staged = this.shouldStageRepaymentOnUndo(repayment)
    this.repaymentsService
      .undoDeleteRepayments([repayment], { signal: this.lifecycleAbort.signal })
      .then(() => {
        this.repaymentsService
          .loadAllRepayments({ signal: this.lifecycleAbort.signal })
          .catch((error) => this.swallowAbort(error))
      })
      .catch((error) => {
        repayment.deleted = previousState.deleted
        repayment.staged = previousState.staged
        this.swallowAbort(error)
      })
  }

  onTableSort(event: { field: string; order: number }) {
    this.sortField = event.field
    this.sortOrder = event.order
    this.tableQueryService.setSort(event.field, event.order)
    this.tableQueryService.resetPagination()
    this.refreshTableData()
  }

  deleteSelectedRepayments(event: any) {
    if (this.destroyed || !this.canDelete) {
      return
    }
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Delete ' + this.selectedRepayments.length + ' repayments?',
      header: 'Delete Repayments',
      icon: 'pi pi-info-circle',

      rejectLabel: 'Cancel',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Delete',
        severity: 'danger',
      },
      key: 'standardPopup',
      accept: () => {
        if (!this.operationState.begin('bulk:delete')) {
          return
        }
        const previousStates = new Map<
          string,
          { deleted: boolean; staged: boolean }
        >()
        this.repayments = this.repayments.filter(
          (repayment) =>
            !(
              this.selectedRepayments.some((r) => r.id === repayment.id) &&
              repayment.isNew
            ),
        )
        this.selectedRepayments = this.selectedRepayments.filter(
          (repayment) => !repayment.isNew,
        )
        const repaymentsToDelete = this.selectedRepayments
        repaymentsToDelete.forEach((repayment) => {
          if (!repayment?.id) {
            return
          }
          previousStates.set(repayment.id, {
            deleted: !!repayment.deleted,
            staged: !!repayment.staged,
          })
          repayment.deleted = true
        })
        if (repaymentsToDelete.length > 0) {
          this.repaymentsService
            .deleteRepayments(repaymentsToDelete, {
              signal: this.lifecycleAbort.signal,
            })
            .then(() => {
              this.repaymentsService
                .loadAllRepayments({ signal: this.lifecycleAbort.signal })
                .catch((error) => this.swallowAbort(error))
              if (!this.destroyed) {
                this.selectedRepayments = []
              }
            })
            .catch((error) => {
              repaymentsToDelete.forEach((repayment) => {
                if (!repayment?.id) {
                  return
                }
                const previous = previousStates.get(repayment.id)
                if (previous) {
                  repayment.deleted = previous.deleted
                  repayment.staged = previous.staged
                }
              })
              this.swallowAbort(error)
            })
            .finally(() => this.operationState.end('bulk:delete'))
        } else {
          this.operationState.end('bulk:delete')
        }
      },
      reject: () => {},
    })
  }

  undoDeleteSelectedRepayments(event: any) {
    if (this.destroyed || !this.canDelete) {
      return
    }
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message:
        'Undo delete on ' + this.selectedRepayments.length + ' repayments?',
      header: 'Undo Delete Repayments',
      icon: 'pi pi-info-circle',

      rejectLabel: 'Cancel',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Undo Delete',
        severity: 'info',
      },
      key: 'standardPopup',
      accept: () => {
        if (!this.operationState.begin('bulk:undo-delete')) {
          return
        }
        const previousStates = new Map<
          string,
          { deleted: boolean; staged: boolean }
        >()
        this.selectedRepayments.forEach((repayment) => {
          if (!repayment?.id) {
            return
          }
          previousStates.set(repayment.id, {
            deleted: !!repayment.deleted,
            staged: !!repayment.staged,
          })
          repayment.deleted = false
          repayment.staged = this.shouldStageRepaymentOnUndo(repayment)
        })
        this.repaymentsService
          .undoDeleteRepayments(this.selectedRepayments, {
            signal: this.lifecycleAbort.signal,
          })
          .then(() => {
            this.repaymentsService
              .loadAllRepayments({ signal: this.lifecycleAbort.signal })
              .catch((error) => this.swallowAbort(error))
            if (!this.destroyed) {
              this.selectedRepayments = []
            }
          })
          .catch((error) => {
            this.selectedRepayments.forEach((repayment) => {
              if (!repayment?.id) {
                return
              }
              const previous = previousStates.get(repayment.id)
              if (previous) {
                repayment.deleted = previous.deleted
                repayment.staged = previous.staged
              }
            })
            this.swallowAbort(error)
          })
          .finally(() => this.operationState.end('bulk:undo-delete'))
      },
      reject: () => {},
    })
  }

  private shouldStageRepaymentOnUndo(repayment: any): boolean {
    const sourceFileId = repayment?.source_file
    if (!sourceFileId) {
      return false
    }
    const file = this.uploadedFileService.files.find(
      (uploaded) => uploaded.id === sourceFileId,
    )
    if (!file?.status) {
      return true
    }
    return file.status.toUpperCase() === 'UNSAVED'
  }

  bulkEditRepayments(_event: Event): void {
    if (
      this.destroyed ||
      !this.canEdit ||
      !this.hasSelectedRepayments
    ) {
      return
    }

    this.bulkEditValues = this.createEmptyBulkEditValues()
    this.bulkEditDialogVisible = true
    void Promise.all([
      this.loadMerchantOptions(),
      this.loadTerminalOptions(),
      this.loadCashAdvanceOptions(),
    ]).finally(() => this.syncDropdownOptions())
  }

  hasBulkEditChanges(): boolean {
    return Object.values(this.bulkEditValues).some(
      (value) => value !== null && value !== '',
    )
  }

  applyBulkEdit(): void {
    if (!this.hasBulkEditChanges() || !this.operationState.begin('bulk:edit')) {
      return
    }

    const update = this.buildBulkEditUpdate()
    // Bulk edits must be durable immediately. Keeping them as local manual
    // saves makes them disappear on navigation or refresh before every row is
    // persisted.
    const request = this.isWholeFileSelection && this.sourceFileId
      ? this.repaymentsService.requestBulkRepaymentEditForFile(this.sourceFileId, update)
      : this.repaymentsService.requestBulkRepaymentEdit(this.selectedRepayments, update)
    request
      .then((job) => {
        this.bulkEditJob = job
        this.bulkEditDialogVisible = false
      })
      .catch((error) => this.handleOperationError('Could not queue repayment updates', error))
      .finally(() => this.operationState.end('bulk:edit'))
  }

  bulkEditProgressPercent(): number {
    if (!this.bulkEditJob) return 0
    return Math.round((this.bulkEditJob.completed_rows / Math.max(1, this.bulkEditJob.total_rows)) * 100)
  }

  private async refreshBulkEditProgress(): Promise<void> {
    if (!this.bulkEditJob || this.destroyed) return
    try {
      const job = await this.repaymentsService.getRepaymentBulkEditJob(this.bulkEditJob.job_id)
      if (!job) return
      this.bulkEditJob = job
      if (job.status === 'COMPLETE') {
        this.bulkEditJob = null
        this.refreshTableData()
        this.messageService.add({ severity: 'success', summary: 'Repayments updated', detail: 'All selected repayments were updated.', life: 4000 })
      } else if (job.status === 'FAILED') {
        this.bulkEditJob = null
        this.notifyError('Repayment update failed', job.error || 'The changes were not completed.')
        this.refreshTableData()
      }
    } catch (error) { this.swallowAbort(error) }
  }

  private async restoreActiveBulkEditJob(): Promise<void> {
    try {
      const [job] = await this.repaymentsService.getActiveRepaymentBulkEditJobs()
      if (!this.destroyed && job) this.bulkEditJob = job
    } catch (error) { this.swallowAbort(error) }
  }

  public get hasPendingChanges(): boolean {
    return this.pendingManualSaveRows.size > 0
  }

  public get shouldSaveAllChangesToFile(): boolean {
    return (
      !!this.sourceFileId &&
      Array.from(this.pendingManualSaveRows.values()).some(
        (repayment) => repayment.staged,
      )
    )
  }

  public get bulkSaveLabel(): string {
    const pendingRepayments = Array.from(this.pendingManualSaveRows.values())
    const hasStagedRepayments =
      !!this.sourceFileId &&
      pendingRepayments.some((repayment) => repayment.staged)

    if (!hasStagedRepayments) {
      return 'Commit all to Salesforce'
    }

    return pendingRepayments.some((repayment) => !repayment.staged)
      ? 'Save changes'
      : 'Save all to File'
  }

  async commitAllChanges(): Promise<void> {
    if (
      this.destroyed ||
      !this.canEdit ||
      !this.hasPendingChanges ||
      !this.operationState.begin('bulk:commit')
    ) {
      return
    }

    const savedToFile = this.shouldSaveAllChangesToFile
    let savedCount = 0
    try {
      // Snapshot the keys because saving a new row replaces its temporary ID.
      const pendingEntries = Array.from(this.pendingManualSaveRows.entries())
      for (const [key, repayment] of pendingEntries) {
        const original =
          this.pendingManualSaveOriginals.get(key) ??
          this.snapshotRepayment(repayment)
        const saved = await this.persistRepaymentChange(repayment, original)
        if (!saved) {
          continue
        }
        this.clearRepaymentPendingManualSave(key)
        this.clearRepaymentPendingManualSave(this.getRepaymentKey(repayment))
        savedCount += 1
      }

      if (savedCount > 0) {
        this.messageService.add({
          severity: 'success',
          summary: savedToFile ? 'Changes saved to file' : 'Changes committed',
          detail: savedToFile
            ? `${savedCount} repayment${savedCount === 1 ? '' : 's'} saved.`
            : `${savedCount} repayment${savedCount === 1 ? '' : 's'} committed to Salesforce.`,
          life: 4000,
        })
      }
      if (!this.isEmbedded && savedCount > 0) {
        this.refreshTableData()
      }
    } finally {
      this.operationState.end('bulk:commit')
    }
  }

  private createEmptyBulkEditValues(): BulkEditValues {
    return {
      repayment_date: null,
      description: null,
      data_source: null,
      x365_split: null,
      comments: null,
      terminal_id: null,
      cash_advance_id: null,
      merchant_id: null,
    }
  }

  private buildBulkEditUpdate(): Partial<Repayment> {
    const values = this.bulkEditValues
    const update: Partial<Repayment> = {
      updated_by: this.authService.getAuditUser(),
    }

    if (values.repayment_date) {
      update.repayment_date = this.normalizeRepaymentDate(values.repayment_date)
    }
    if (values.description) update.description = values.description
    if (values.data_source) update.data_source = values.data_source
    if (values.x365_split !== null) update.x365_split = values.x365_split
    if (values.comments) update.comments = values.comments
    if (values.terminal_id) update.terminal_id = values.terminal_id
    if (values.cash_advance_id) update.cash_advance_id = values.cash_advance_id
    if (values.merchant_id) update.merchant_id = values.merchant_id

    return update
  }

  private applyBulkEditToRepayment(
    repayment: Repayment,
    update: Partial<Repayment>,
  ): void {
    Object.assign(repayment, update)
    if (update.repayment_date) {
      repayment.repayment_date = this.normalizeRepaymentDate(update.repayment_date)
    }
    if (update.merchant_id) {
      repayment.merchants = this.merchantOptions.find(
        (merchant) => merchant.id === update.merchant_id,
      )
    }
    if (update.cash_advance_id) {
      repayment.cash_advances = this.cashAdvanceOptions.find(
        (cashAdvance) => cashAdvance.id === update.cash_advance_id,
      )
    }
    if (update.terminal_id) {
      const terminal = this.terminalOptions.find(
        (item) => item.id === update.terminal_id,
      )
      repayment.terminals = terminal
      repayment.terminal_name = terminal?.name ?? null
    }
  }

  toggleDeletedRepayments(table: Table) {
    if (this.destroyed || !this.canView) {
      return
    }
    const nextShowDeleted = !this.showDeletedOnly
    this.applyDeletedRepaymentsFilter(nextShowDeleted, table, {
      applyTableFilter: false,
    })
    // PrimeNG can normalize the table filter object during a refresh. Keep the
    // server-side query state authoritative so a file-scoped request always
    // receives the deleted predicate selected by this button.
    this.tableQueryService.setFilter(
      'deleted',
      this.createDeletedFilterConstraint(nextShowDeleted),
    )
    this.tableQueryService.resetPagination()
    if (!nextShowDeleted) {
      this.repaymentsService.resetRepaymentsCache()
    }
    this.refreshTableData({
      deletedOnly: nextShowDeleted,
      excludeDeletedStaging:
        nextShowDeleted && this.isCurrentSourceFileCommitted(),
      committedSourceFile: this.isCurrentSourceFileCommitted(),
    })
  }

  toggleMissingFields(table: Table): void {
    if (this.destroyed || !this.canView) {
      return
    }

    this.missingFieldsOnly = !this.missingFieldsOnly
    this.tableQueryService.setMissingRelationFieldsFilter(
      this.missingFieldsOnly,
    )
    this.tableQueryService.resetPagination()
    this.refreshTableData()
  }

  promoteRepayment(repayment: Repayment, event: Event): void {
    event.stopPropagation()
    if (this.destroyed || !this.canEdit) {
      return
    }

    const operationKey = this.repaymentActionKey(repayment, 'promote')
    if (!this.operationState.begin(operationKey)) {
      return
    }

    const missing: string[] = []
    if (!repayment.description?.trim()) {
      missing.push('Description')
    }
    if (!repayment.merchant_id?.trim()) {
      missing.push('Merchant')
    }
    if (
      isTerminalRequired(repayment.description, repayment.data_source) &&
      !repayment.terminal_id?.trim()
    ) {
      missing.push('Terminal')
    }
    if (!repayment.cash_advance_id?.trim()) {
      missing.push('Cash Advance')
    }
    if (!repayment.repayment_date?.toString().trim()) {
      missing.push('Repayment Date')
    }
    if (
      repayment.payment_amount === null ||
      repayment.payment_amount === undefined ||
      repayment.payment_amount === ''
    ) {
      missing.push('Amount')
    }

    if (missing.length > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Cannot save repayment',
        detail: `The following fields are required: ${missing.join(', ')}`,
        life: 6000,
      })
      this.operationState.end(operationKey)
      return
    }

    this.repaymentsService
      .promoteRepaymentFromStaging(repayment, {
        signal: this.lifecycleAbort.signal,
      })
      .then(() => {
        if (this.destroyed) {
          return
        }
        // Remove the staged row immediately. The subsequent staging refresh keeps
        // the table authoritative, but must never leave a cleared ghost row visible.
        this.repayments = this.repayments.filter(
          (candidate) => candidate.id !== repayment.id,
        )
        this.selectedRepayments = (this.selectedRepayments ?? []).filter(
          (candidate) => candidate.id !== repayment.id,
        )
        if (this.totalRepaymentsForQuery !== null) {
          this.totalRepaymentsForQuery = Math.max(
            0,
            this.totalRepaymentsForQuery - 1,
          )
        }
        this.messageService.add({
          severity: 'success',
          summary: 'Repayment saved',
          detail: 'The repayment has been moved to All Repayments.',
          life: 4000,
        })
        this.refreshTableData()
      })
      .catch((error) => {
        if (this.destroyed) {
          return
        }
        console.error(
          '[RepaymentsTableComponent] promoteRepayment error',
          error,
        )
        this.handleOperationError('Could not save repayment', error)
      })
      .finally(() => this.operationState.end(operationKey))
  }

  async saveRepaymentRow(repayment: Repayment, event: Event): Promise<void> {
    event.stopPropagation()
    if (this.destroyed || !this.canEdit) {
      return
    }

    const validationMessage = repayment.isNew
      ? this.validateNewRepayment(repayment)
      : this.validateRepaymentAmount(repayment)

    if (validationMessage) {
      this.messageService.add({
        severity: 'warn',
        summary: repayment.isNew
          ? 'Cannot save new repayment'
          : 'Cannot save repayment',
        detail: validationMessage,
        life: 6000,
      })
      return
    }

    const originalKey = this.getRepaymentKey(repayment)
    const snapshot =
      this.pendingManualSaveOriginals.get(originalKey) ??
      this.snapshotRepayment(repayment)

    const saveSucceeded = await this.persistRepaymentChange(repayment, snapshot)
    if (!saveSucceeded) {
      return
    }

    this.clearRepaymentPendingManualSave(originalKey)
    this.clearRepaymentPendingManualSave(this.getRepaymentKey(repayment))

    if (!this.isEmbedded) {
      this.refreshTableData()
    }
  }

  get allSelectedDeleted(): boolean {
    return (
      this.selectedRepayments?.length > 0 &&
      this.selectedRepayments.every((r) => r.deleted)
    )
  }

  filterTable(
    filterId: string,
    filterVals: Array<{ name: string }>,
    options?: { refresh?: boolean; emitQuickFilter?: boolean },
  ): boolean {
    if (this.destroyed) {
      return false
    }
    if (this.areQuickFilterValuesUnchanged(filterId, filterVals)) {
      return false
    }
    const shouldRefresh = options?.refresh ?? true
    const shouldEmitQuickFilter = options?.emitQuickFilter ?? true
    if (filterVals) {
      const normalizedValues = filterVals
        .map((val) => String(val?.name ?? '').trim())
        .filter(Boolean)
      if (shouldEmitQuickFilter) {
        this.quickFilterClicked.emit()
      }
      this.dataTableChild.filters[filterId] =
        normalizedValues.length > 1
          ? [
              {
                value: normalizedValues,
                matchMode: 'in',
                operator: 'or',
              },
            ]
          : normalizedValues.length === 1
            ? [
                {
                  value: normalizedValues[0],
                  matchMode: 'equals',
                  operator: 'or',
                },
              ]
            : [{ value: null, matchMode: 'startsWith', operator: 'and' }]
      this.tableQueryService.setFilters(this.dataTableChild.filters ?? {})
      this.tableQueryService.resetPagination()
      if (shouldRefresh) {
        this.refreshTableData()
      }
      return true
    } else {
      this.dataTableChild.filters[filterId] = [
        { value: null, matchMode: 'startsWith', operator: 'and' },
      ]
      this.tableQueryService.setFilters(this.dataTableChild.filters ?? {})
      this.tableQueryService.resetPagination()
      if (shouldRefresh) {
        this.refreshTableData()
      }
      return true
    }
  }

  refreshQuickFilterData(): Promise<void> {
    return this.refreshTableData()
  }

  private areQuickFilterValuesUnchanged(
    filterId: string,
    filterVals: Array<{ name: string }> | undefined,
  ): boolean {
    const existing = this.dataTableChild?.filters?.[filterId]
    const existingEntries = Array.isArray(existing)
      ? existing
      : existing
        ? [existing]
        : []
    const existingValues = existingEntries
      .flatMap((entry: any) =>
        Array.isArray(entry?.value)
          ? entry.value.map((value: unknown) => String(value ?? '').trim())
          : [String(entry?.value ?? '').trim()],
      )
      .filter(Boolean)
      .sort()
    const nextValues = (filterVals ?? [])
      .map((entry) => String(entry?.name ?? '').trim())
      .filter(Boolean)
      .sort()

    if (existingValues.length !== nextValues.length) {
      return false
    }

    return existingValues.every((value, index) => value === nextValues[index])
  }

  public isEmbeddedLockedField(field: EditableField): boolean {
    if (!this.isEmbedded) {
      return false
    }
    switch (field) {
      case 'merchant_id':
        return !!this.embeddedPresetValues.merchant_id
      case 'terminal_id':
        return !!this.embeddedPresetValues.terminal_id
      case 'cash_advance_id':
        return false
      default:
        return false
    }
  }

  private resolveSingleEmbedId(
    param: string | string[] | undefined,
  ): string | undefined {
    if (!param) {
      return undefined
    }
    const ids = Array.isArray(param)
      ? param
      : String(param)
          .split(',')
          .map((s) => s.trim())
    return ids.length === 1 ? ids[0] : undefined
  }

  addEmptyRow() {
    if (this.destroyed || !this.canEdit) {
      return
    }
    if (!this.isInProgress && !this.isEmbedded) {
      this.router.navigate(['in-progress'], { queryParams: { new: '1' } })
      return
    }
    // Temporary ID generation using timestamp
    const id = new Date().getTime().toString()
    const presetMerchantId = this.embeddedPresetValues.merchant_id ?? ''
    const presetTerminalId = this.embeddedPresetValues.terminal_id ?? ''
    const presetCashAdvanceId = this.embeddedPresetValues.cash_advance_id ?? ''
    const emptyRow: Repayment = {
      id: id,
      description: '',
      comments: '',
      repayment_date: this.normalizeRepaymentDate(new Date()),
      payment_amount: '',
      balance: '',
      merchant_id: presetMerchantId,
      cash_advance_id: presetCashAdvanceId,
      terminal_id: presetTerminalId,
      source_file: '',
      row_number: 0,
      updated_by: this.authService.getAuditUser() ?? '',
      deleted: false,
      staged: !this.isEmbedded,
      isNew: true,
      country: this.repaymentsService.country ?? 'uk',
    }
    if (presetMerchantId) {
      const merchant = this.merchantOptions.find(
        (m) => m.id === presetMerchantId,
      )
      if (merchant) {
        emptyRow.merchants = { ...merchant }
      }
    }
    if (presetTerminalId) {
      const terminal = this.terminalOptions.find(
        (t) => t.id === presetTerminalId,
      )
      if (terminal) {
        emptyRow.terminals = { ...terminal }
        emptyRow.terminal_name = terminal.name
      }

      if (!emptyRow.merchant_id) {
        const terminalWithMerchant = this.repaymentsService.terminals?.find(
          (item) => item?.id === presetTerminalId,
        )
        const inferredMerchantId =
          this.getTerminalMerchantId(terminalWithMerchant) ?? ''

        if (inferredMerchantId) {
          emptyRow.merchant_id = inferredMerchantId
          const merchant =
            this.merchantOptions.find((m) => m.id === inferredMerchantId) ??
            this.repaymentsService.merchants?.find(
              (m) => m?.id === inferredMerchantId,
            )
          if (merchant) {
            emptyRow.merchants = {
              id: String(merchant.id),
              name: String(merchant.name),
            }
          }
        }
      }
    }
    if (presetCashAdvanceId) {
      const cashAdvance = this.cashAdvanceOptions.find(
        (c) => c.id === presetCashAdvanceId,
      )
      if (cashAdvance) {
        emptyRow.cash_advances = { ...cashAdvance }
      }

      // In embed mode, if cash advance is preset but merchant is not,
      // infer the merchant from the selected cash advance.
      if (!emptyRow.merchant_id) {
        const cashAdvanceWithMerchant =
          this.repaymentsService.cashAdvances?.find(
            (item) => item?.id === presetCashAdvanceId,
          )
        const inferredMerchantId =
          typeof cashAdvanceWithMerchant?.merchant_id === 'string'
            ? cashAdvanceWithMerchant.merchant_id
            : ''

        if (inferredMerchantId) {
          emptyRow.merchant_id = inferredMerchantId
          const merchant =
            this.merchantOptions.find((m) => m.id === inferredMerchantId) ??
            this.repaymentsService.merchants?.find(
              (m) => m?.id === inferredMerchantId,
            )
          if (merchant) {
            emptyRow.merchants = {
              id: String(merchant.id),
              name: String(merchant.name),
            }
          }
        }
      }
    }

    if (this.isEmbedded && !emptyRow.merchant_id && presetTerminalId) {
      void this.repaymentsService
        .getMerchantIdForTerminal(presetTerminalId, {
          signal: this.lifecycleAbort.signal,
        })
        .then((merchantId) => {
          if (this.destroyed || !merchantId || emptyRow.merchant_id) {
            return
          }

          emptyRow.merchant_id = merchantId
          const merchant =
            this.merchantOptions.find((m) => m.id === merchantId) ??
            this.repaymentsService.merchants?.find((m) => m?.id === merchantId)
          if (merchant) {
            emptyRow.merchants = {
              id: String(merchant.id),
              name: String(merchant.name),
            }
          }
          this.repayments = [...this.repayments]
        })
        .catch((error) => this.swallowAbort(error))
    }

    if (this.isEmbedded && !emptyRow.merchant_id && presetCashAdvanceId) {
      void this.repaymentsService
        .getMerchantIdForCashAdvance(presetCashAdvanceId, {
          signal: this.lifecycleAbort.signal,
        })
        .then((merchantId) => {
          if (this.destroyed || !merchantId || emptyRow.merchant_id) {
            return
          }

          emptyRow.merchant_id = merchantId
          const merchant =
            this.merchantOptions.find((m) => m.id === merchantId) ??
            this.repaymentsService.merchants?.find((m) => m?.id === merchantId)
          if (merchant) {
            emptyRow.merchants = {
              id: String(merchant.id),
              name: String(merchant.name),
            }
          }
          this.repayments = [...this.repayments]
        })
        .catch((error) => this.swallowAbort(error))
    }

    this.repayments = [emptyRow, ...this.repayments]
    if (this.shouldUseManualSaveMode()) {
      this.markRepaymentPendingManualSave(
        emptyRow,
        this.snapshotRepayment(emptyRow),
      )
    }
    this.activateCellEdit(emptyRow, 'description')
  }

  ngOnDestroy(): void {
    this.clearColumnHoverEdge()
    this.detachScrollListener()
    this.destroyed = true
    this.activeQueryAbort?.abort()
    this.activeQueryAbort = null
    this.destroy$.next()
    this.destroy$.complete()
    this.closeAllColumnFilters()
    this.lifecycleAbort.abort()
    if (this.bulkEditProgressPoll) clearInterval(this.bulkEditProgressPoll)
    if (this.isEmbedded) {
      document.body.classList.remove('embedded')
      this.tableQueryService.setRepaymentDateSecondarySorts([])
    }
    RepaymentsTableComponent.instanceCount--
    console.warn(
      `[RepaymentsTableComponent] destroyed instance ${this.instanceId} remaining=${RepaymentsTableComponent.instanceCount}`,
    )
  }


  private isAbortError(error: unknown): boolean {
    return (
      !!error &&
      typeof error === 'object' &&
      'name' in error &&
      (error as { name?: string }).name === 'AbortError'
    )
  }

  private applyColumnHoverEdge(
    tableRoot: HTMLElement,
    columnIndex: number,
  ): void {
    if (
      this.highlightedTableRoot === tableRoot &&
      this.highlightedColumnIndex === columnIndex
    ) {
      return
    }

    this.clearColumnHoverEdge()

    const columnCells = tableRoot.querySelectorAll(
      `.p-datatable-thead > tr > th:nth-child(${columnIndex}), .p-datatable-tbody > tr > td:nth-child(${columnIndex})`,
    )

    columnCells.forEach((cell) => {
      this.renderer.addClass(cell, 'column-edge-hover')
    })

    this.highlightedTableRoot = tableRoot
    this.highlightedColumnIndex = columnIndex
  }

  private clearColumnHoverEdge(): void {
    if (!this.highlightedTableRoot) {
      this.highlightedColumnIndex = null
      return
    }

    this.highlightedTableRoot
      .querySelectorAll('.column-edge-hover')
      .forEach((cell) => {
        this.renderer.removeClass(cell, 'column-edge-hover')
      })

    this.highlightedTableRoot = null
    this.highlightedColumnIndex = null
  }

  private swallowAbort(error: unknown) {
    if (this.isAbortError(error)) {
      return
    }

    if (this.isSecretRefreshFailure(error)) {
      const detail =
        error instanceof Error
          ? error.message
          : 'The PostgREST secret expired and could not be refreshed.'
      this.notifyError('Session expired', detail)
      return
    }

    this.handleOperationError('Request failed', error)
  }

  private handleOperationError(
    summary: string,
    error: unknown,
    fallback = 'Please try again.',
  ): void {
    if (this.isAbortError(error) || this.destroyed) {
      return
    }
    this.notifyError(
      summary,
      error instanceof Error && error.message ? error.message : fallback,
    )
  }

  private isSecretRefreshFailure(error: unknown): boolean {
    if (!(error instanceof Error)) {
      return false
    }

    const message = error.message.toLowerCase()
    return (
      message.includes('postgrest secret expired') ||
      message.includes('embedded session secret expired')
    )
  }

  private beginQueryRequest(): AbortSignal {
    this.activeQueryAbort?.abort()
    const controller = new AbortController()
    this.activeQueryAbort = controller
    return controller.signal
  }

  private logRapidCalls(label: string, thresholdMs = 300) {
    const now = Date.now()
    const last = this.lastCallMap.get(label)
    this.lastCallMap.set(label, now)
    if (last && now - last < thresholdMs) {
      console.warn(`[RepaymentsTableComponent] rapid call: ${label}`)
    }
  }

  private closeAllColumnFilters(): void {
    this.columnFilters?.forEach((filter) => {
      try {
        filter.hide()
      } catch {
        // ignore
      }
    })
  }

  private applyDeletedRepaymentsFilter(
    showDeletedOnly: boolean,
    table: Table,
    options: { applyTableFilter?: boolean; syncQueryFilters?: boolean } = {},
  ): void {
    this.showDeletedOnly = showDeletedOnly
    table.filters ??= {}
    table.filters['deleted'] = [
      this.createDeletedFilterConstraint(showDeletedOnly),
    ]

    if (options.syncQueryFilters ?? true) {
      this.tableQueryService.setFilters(table.filters ?? {})
    }

    if (options.applyTableFilter ?? true) {
      table._filter()
    }
  }

  private createDeletedFilterConstraint(
    showDeletedOnly: boolean,
  ): TableFilterConstraint {
    return {
      value: showDeletedOnly,
      matchMode: 'equals',
      operator: 'and',
    }
  }

  private async refreshTableData({
    append = false,
    deletedOnly,
    excludeDeletedStaging = false,
    committedSourceFile = false,
  }: {
    append?: boolean
    deletedOnly?: boolean
    excludeDeletedStaging?: boolean
    committedSourceFile?: boolean
  } = {}): Promise<void> {
    const refreshGeneration = ++this.refreshGeneration
    const isCurrentRefresh = () =>
      !this.destroyed && this.refreshGeneration === refreshGeneration

    // this.logRapidCalls('refreshTableData')
    if (!this.canReadRepayments) {
      this.clearRepaymentsForNoView()
      return
    }

    const cacheComplete = this.repaymentsService.isCacheComplete()
    const isDefaultState = this.tableQueryService.isDefaultState()
    const hasGlobalFilter = this.tableQueryService.hasGlobalFilter()
    const hasTableFilters = this.tableQueryService.hasActiveFilters()
    if (!append) {
      this.totalRepaymentsForQuery = null
    }
    // ensure we consider new queries
    this.pendingQueryToken = null

    if (this.sourceFileId && isDefaultState) {
      const queryToken = Symbol('repayments-table-file-query')
      this.pendingQueryToken = queryToken
      this.internalLoading = true
      const requestSignal = this.beginQueryRequest()

      try {
        const query = this.withDeletedRepaymentsPredicate(
          await this.tableQueryService.buildPostgrestParams(
            this.repaymentsService.buildBaseQueryParams(),
          ),
          deletedOnly,
        )
        const countQuery = this.withDeletedRepaymentsPredicate(
          await this.tableQueryService.buildPostgrestParams(
            this.repaymentsService.buildBaseQueryParams(),
            { includePagination: false },
          ),
          deletedOnly,
        )
        if (!isCurrentRefresh() || this.pendingQueryToken !== queryToken) {
          return
        }
        const [countResult, repayments] = await Promise.all([
          this.repaymentsService.fetchRepaymentsCountWithQuery(countQuery, {
            signal: requestSignal,
            excludeDeletedStaging,
            committedSourceFile,
          }),
          this.repaymentsService.fetchRepaymentsWithQuery(query, {
            signal: requestSignal,
            skipCacheUpdate: true,
            excludeDeletedStaging,
            committedSourceFile,
          }),
        ])

        if (!isCurrentRefresh() || this.pendingQueryToken !== queryToken) {
          return
        }

        this.repayments = append
          ? [...this.repayments, ...this.normalizeRepaymentsForView(repayments)]
          : this.withPendingManualSaves(
              this.normalizeRepaymentsForView(repayments),
            )
        this.syncDropdownOptions()
        this.totalRepaymentsForQuery = countResult
        this.updateHasMoreRepayments()
      } catch (error) {
        this.swallowAbort(error)
      } finally {
        if (!this.destroyed && this.pendingQueryToken === queryToken) {
          this.internalLoading = false
          this.pendingQueryToken = null
          this.finishLoadMoreCycle()
          this.updateHasMoreRepayments()
          if (this.activeQueryAbort?.signal === requestSignal) {
            this.activeQueryAbort = null
          }
        }
      }
      return
    }

    if (cacheComplete && !hasGlobalFilter && !hasTableFilters) {
      if (isDefaultState) {
        const sorted = this.repaymentsService.getFilteredRepaymentsFromCache()
        this.repayments = this.withPendingManualSaves(
          this.normalizeRepaymentsForView(sorted),
        )
      } else {
        const filtered = this.repaymentsService.getFilteredRepaymentsFromCache()
        const normalized = this.normalizeRepaymentsForView(filtered)
        this.repayments = append
          ? [...this.repayments, ...normalized]
          : this.withPendingManualSaves(normalized)
      }
      this.syncDropdownOptions()
      this.totalRepaymentsForQuery = this.repayments.length
      this.updateHasMoreRepayments()
      this.pendingQueryToken = null
      return
    }

    if (isDefaultState) {
      // If we are returning to the default view, reset and fetch the canonical dataset.
      this.repaymentsService.resetRepaymentsCache()
      try {
        this.internalLoading = true
        await this.repaymentsService.loadAllRepayments({
          signal: this.lifecycleAbort.signal,
        })
      } catch (error) {
        this.swallowAbort(error)
      } finally {
        if (isCurrentRefresh()) {
          this.internalLoading = false
        }
      }
      if (!isCurrentRefresh()) {
        return
      }
      const sorted = this.repaymentsService.getFilteredRepaymentsFromCache()
      this.repayments = this.withPendingManualSaves(
        this.normalizeRepaymentsForView(sorted),
      )
      this.syncDropdownOptions()
      this.totalRepaymentsForQuery = this.repaymentsService.isCacheComplete()
        ? this.repayments.length
        : null
      this.updateHasMoreRepayments()
      this.pendingQueryToken = null
      return
    }

    const queryToken = Symbol('repayments-table-query')
    this.pendingQueryToken = queryToken
    this.internalLoading = true
    const requestSignal = this.beginQueryRequest()

    let query: string

    try {
      query = this.withDeletedRepaymentsPredicate(
        await this.tableQueryService.buildPostgrestParams(
          this.repaymentsService.buildBaseQueryParams(),
        ),
        deletedOnly,
      )
    } catch (error) {
      console.error('Failed to build repayments query params', error)
      if (!this.destroyed && this.pendingQueryToken === queryToken) {
        this.internalLoading = false
        this.pendingQueryToken = null
        if (this.activeQueryAbort?.signal === requestSignal) {
          this.activeQueryAbort = null
        }
      }
      return
    }

    // A newer route/query-param update may have started while schema metadata
    // was loading. Do not let this stale request replace its result.
    if (!isCurrentRefresh()) {
      return
    }

    if (!query) {
      if (!this.destroyed && this.pendingQueryToken === queryToken) {
        this.internalLoading = false
        this.pendingQueryToken = null
        if (this.activeQueryAbort?.signal === requestSignal) {
          this.activeQueryAbort = null
        }
      }
      this.updateHasMoreRepayments()
      return
    }
    const shouldFetchCount = !append || this.totalRepaymentsForQuery === null

    try {
      const [countResult, repayments] = await Promise.all([
        shouldFetchCount
          ? this.repaymentsService.fetchRepaymentsCountWithQuery(query, {
              signal: requestSignal,
              excludeDeletedStaging,
              committedSourceFile,
            })
          : Promise.resolve(null),
        this.repaymentsService.fetchRepaymentsWithQuery(query, {
          signal: requestSignal,
          excludeDeletedStaging,
          committedSourceFile,
        }),
      ])

      if (!isCurrentRefresh() || this.pendingQueryToken !== queryToken) {
        return
      }

      if (countResult !== null && countResult !== undefined) {
        this.totalRepaymentsForQuery = countResult
      }

      this.repayments = append
        ? [...this.repayments, ...this.normalizeRepaymentsForView(repayments)]
        : this.withPendingManualSaves(
            this.normalizeRepaymentsForView(repayments),
          )
      this.syncDropdownOptions()
      this.updateHasMoreRepayments()
    } catch (error) {
      this.swallowAbort(error)
    } finally {
      if (!this.destroyed && this.pendingQueryToken === queryToken) {
        this.internalLoading = false
        this.pendingQueryToken = null
        this.finishLoadMoreCycle()
        this.updateHasMoreRepayments()
        if (this.activeQueryAbort?.signal === requestSignal) {
          this.activeQueryAbort = null
        }
      }
    }
  }

  private advancePagination(): void {
    const state = this.tableQueryService.getQueryState()
    this.tableQueryService.setPagination(
      state.offset + state.limit,
      state.limit,
    )
  }

  private withDeletedRepaymentsPredicate(
    query: string,
    deletedOnly: boolean | undefined,
  ): string {
    if (deletedOnly === undefined) {
      return query
    }
    const params = new URLSearchParams(query)
    params.delete('deleted')
    params.set('deleted', `eq.${deletedOnly}`)
    return params.toString()
  }

  private isCurrentSourceFileCommitted(): boolean {
    if (this.isPersistedSourceFile(this.sourceFileStatus)) {
      return true
    }
    if (!this.sourceFileId) {
      return false
    }
    const file = this.uploadedFileService.files.find(
      (candidate) => candidate.id === this.sourceFileId,
    )
    return this.isPersistedSourceFile(file?.status)
  }

  private isPersistedSourceFile(status: string | null | undefined): boolean {
    const normalizedStatus = status?.toUpperCase()
    return normalizedStatus === 'SAVED' || normalizedStatus === 'PROCESSED'
  }

  private updateHasMoreRepayments(countOverride?: number | null): void {
    const currentRepayments = Array.isArray(this.repayments)
      ? this.repayments
      : []
    const total =
      typeof countOverride === 'number'
        ? countOverride
        : this.totalRepaymentsForQuery

    if (typeof total === 'number') {
      this.hasMoreRepayments = currentRepayments.length < total
      return
    }

    if (this.isLoading || this.pendingQueryToken) {
      // Only mark "has more" when actively appending (load-more scroll) or when
      // there is no data yet (initial / blank-slate load). This prevents ghost
      // skeleton rows from appearing below already-visible filtered results while
      // a background re-fetch is in flight.
      this.hasMoreRepayments = this.loadMore || currentRepayments.length === 0
      return
    }

    if (this.tableQueryService.isDefaultState()) {
      this.hasMoreRepayments = !this.repaymentsService.isCacheComplete()
      return
    }

    // Filtered data provided without a known total: assume complete to avoid ghost skeleton rows.
    this.hasMoreRepayments = false
  }

  loadMore = false

  private finishLoadMoreCycle(): void {
    this.loadMore = false
    // Keep the bottom latch set until the scroll position actually leaves the
    // bottom. Layout changes during an append can emit another scroll event;
    // clearing it here would turn one idle bottom position into a request loop.
  }

  private handleLoadMore(): void {
    this.logRapidCalls('handleLoadMore')
    if (this.destroyed || (!this.hasMoreRepayments && !this.isLoading)) {
      return
    }

    this.loadMore = true
    if (this.sourceFileId && this.tableQueryService.isDefaultState()) {
      this.advancePagination()
      this.refreshTableData({ append: true }).finally(() => {
        this.finishLoadMoreCycle()
      })
      return
    }

    if (this.tableQueryService.isDefaultState()) {
      this.repaymentsService
        .loadMoreRepayments({
          signal: this.lifecycleAbort.signal,
        })
        .then(() => {
          if (this.destroyed) {
            return
          }
          this.repayments = this.withPendingManualSaves(
            this.normalizeRepaymentsForView(this.repaymentsService.repayments),
          )
          this.updateHasMoreRepayments()
        })
        .catch((error) => {
          this.finishLoadMoreCycle()
          return this.swallowAbort(error)
        })
        .finally(() => {
          this.finishLoadMoreCycle()
        })
      return
    }

    this.advancePagination()
    this.refreshTableData({ append: true }).finally(() => {
      this.finishLoadMoreCycle()
    })
  }

  private registerScrollListener(): void {
    if (this.scrollContainer) {
      return
    }

    const container = this.resolveScrollContainer()

    if (!container) {
      return
    }

    this.scrollContainer = container

    this.scrollContainer.addEventListener('scroll', this.onTableScroll, {
      passive: true,
    })
  }

  private detachScrollListener(): void {
    if (!this.scrollContainer) {
      return
    }

    this.scrollContainer.removeEventListener('scroll', this.onTableScroll)
    this.scrollContainer = undefined
  }

  private readonly onTableScroll = (event: Event): void => {
    const target = event.target as HTMLElement | null

    if (!target) {
      return
    }

    const distanceToBottom =
      target.scrollHeight - 200 - target.scrollTop - target.clientHeight
    const reachedBottom = distanceToBottom <= 1

    if (
      reachedBottom &&
      !this.isAtBottom &&
      !this.loadMore &&
      !this.isLoading &&
      this.hasMoreRepayments
    ) {
      this.isAtBottom = true
      this.handleLoadMore()
      this.scrolledToBottom.emit()
    } else if (!reachedBottom && this.isAtBottom) {
      this.isAtBottom = false
    }
  }

  private resolveScrollContainer(): HTMLElement | undefined {
    const tableContainer =
      this.dataTableChild?.containerViewChild?.nativeElement ??
      this.dataTableChild?.el?.nativeElement

    if (!tableContainer) {
      return undefined
    }

    const scrollSelectors = ['.p-datatable-table-container']

    for (const selector of scrollSelectors) {
      const candidate = tableContainer.querySelector(selector)
      if (candidate) {
        return candidate
      }
    }

    return tableContainer as HTMLElement
  }

  onDateChange(repayment: Repayment, value: Date | string | null) {
    const normalized = this.normalizeRepaymentDate(value)
    repayment.repayment_date = normalized
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    if (!this.activeEditCell || this.destroyed) {
      return
    }

    if (this.isWithinEditFence(event)) {
      return
    }

    this.handleEditFenceExit()
  }

  @HostListener('document:mousemove', ['$event'])
  onDocumentMouseMove(event: MouseEvent): void {
    if (!this.activeEditCell || this.destroyed) {
      return
    }

    if (this.isWithinEditFence(event)) {
      return
    }

    this.handleEditFenceExit()
  }

  private handleEditFenceExit(): void {
    if (!this.activeEditCell) {
      return
    }

    if (this.activeEditCell.field !== 'payment_amount') {
      this.cancelActiveCellEdit()
      return
    }

    const activeRepayment = this.repayments?.find(
      (repayment) => String(repayment.id) === this.activeEditCell?.repaymentId,
    )

    if (!activeRepayment) {
      this.cancelActiveCellEdit()
      return
    }

    void this.saveActiveCell(activeRepayment)
  }

  isCellEditing(repayment: Repayment, field: EditableField): boolean {
    return (
      !!this.activeEditCell &&
      this.activeEditCell.repaymentId === String(repayment.id) &&
      this.activeEditCell.field === field
    )
  }

  activateCellEdit(
    repayment: Repayment,
    field: EditableField,
    event?: Event,
  ): void {
    event?.stopPropagation()
    if (!this.canEdit) {
      return
    }
    if (repayment.deleted) {
      return
    }

    if (field === 'terminal_id' && this.isTerminalNotApplicable(repayment)) {
      return
    }

    if (field === 'x365_split' && this.is365SplitNotApplicable(repayment)) {
      return
    }

    if (this.isEmbeddedLockedField(field)) {
      return
    }

    if (this.isCellEditing(repayment, field)) {
      return
    }

    this.ensureEditableOptionsLoaded(field, repayment)
    this.shouldFlipActiveRelationDropdown =
      this.isRelationDropdownField(field) &&
      this.shouldFlipRelationDropdown(field, event?.currentTarget)
    this.activeEditCell = {
      repaymentId: String(repayment.id),
      field,
    }
    this.editDraftValue = this.getEditableFieldValue(repayment, field)

    if (field === 'payment_amount' || field === 'x365_split') {
      this.focusActiveAmountInput()
    }
  }

  activateRelationCellEdit(
    repayment: Repayment,
    field: 'merchant_id' | 'terminal_id' | 'cash_advance_id',
    event: MouseEvent,
  ): void {
    if (this.hasSelectedRelationText(event.currentTarget)) {
      event.stopPropagation()
      return
    }

    this.activateCellEdit(repayment, field, event)
  }

  private hasSelectedRelationText(target: EventTarget | null): boolean {
    if (!(target instanceof Node)) {
      return false
    }

    const selection = window.getSelection()
    return Boolean(
      selection &&
        !selection.isCollapsed &&
        (target.contains(selection.anchorNode) || target.contains(selection.focusNode)),
    )
  }

  private focusActiveAmountInput(): void {
    setTimeout(() => {
      const activeInput = document.querySelector(
        '.editable-cell-active .editable-cell-input',
      ) as HTMLInputElement | null

      if (!activeInput) {
        return
      }

      activeInput.focus()
      activeInput.select()
    })
  }

  cancelActiveCellEdit(event?: Event): void {
    event?.stopPropagation()
    this.activeEditCell = null
    this.editDraftValue = null
    this.shouldFlipActiveRelationDropdown = false
  }

  private isRelationDropdownField(
    field: EditableField,
  ): field is 'merchant_id' | 'terminal_id' | 'cash_advance_id' {
    return (
      field === 'merchant_id' ||
      field === 'terminal_id' ||
      field === 'cash_advance_id'
    )
  }

  private shouldFlipRelationDropdown(
    field: 'merchant_id' | 'terminal_id' | 'cash_advance_id',
    target: EventTarget | null,
  ): boolean {
    if (!(target instanceof HTMLElement)) {
      return false
    }

    const tableRoot = this.dataTableChild?.el?.nativeElement as
      | HTMLElement
      | undefined
    const tableViewport = tableRoot?.querySelector(
      '.p-datatable-table-container',
    )

    if (!(tableViewport instanceof HTMLElement)) {
      return false
    }

    const targetRect = target.getBoundingClientRect()
    const viewportRect = tableViewport.getBoundingClientRect()
    return (
      targetRect.right + RELATION_DROPDOWN_PANEL_WIDTH_PX[field] >
      viewportRect.right
    )
  }

  onDescriptionDropdownChange(
    repayment: Repayment,
    event: { value: string | null; originalEvent?: Event },
  ): void {
    const nextValue = event?.value ?? null

    if (
      this.isNonTerminalDescription(nextValue) &&
      this.hasTerminalValue(repayment)
    ) {
      const normalizedValue = (nextValue ?? '').trim().toLowerCase()
      this.confirmationService.confirm({
        target:
          (event?.originalEvent?.target as EventTarget | null) ??
          (event?.originalEvent?.currentTarget as EventTarget | null) ??
          document.body,
        message: `Setting this repayment to ${normalizedValue} will clear the terminal. Continue?`,
        header: 'Clear Terminal',
        icon: 'pi pi-info-circle',
        rejectLabel: 'Cancel',
        rejectButtonProps: {
          label: 'Cancel',
          severity: 'secondary',
          outlined: true,
        },
        acceptButtonProps: {
          label: 'Confirm',
          severity: 'danger',
        },
        key: 'standardPopup',
        accept: () => {
          void this.applyDescriptionSelection(repayment, nextValue, true)
        },
      })
      return
    }

    void this.applyDescriptionSelection(repayment, nextValue, false)
  }

  onEditableDropdownChange(
    repayment: Repayment,
    value: string | null,
  ): Promise<void> {
    this.editDraftValue = value
    return this.saveActiveCell(repayment)
  }

  onEditableDateChange(
    repayment: Repayment,
    value: Date | string | null,
  ): Promise<void> {
    this.editDraftValue = value
    return this.saveActiveCell(repayment)
  }

  onEditableAmountEnter(event: Event, repayment: Repayment): Promise<void> {
    event.preventDefault()
    return this.saveActiveCell(repayment)
  }

  async saveActiveCell(repayment: Repayment): Promise<void> {
    if (!this.activeEditCell || this.destroyed || !this.canEdit) {
      return
    }

    const field = this.activeEditCell.field
    if (field === 'x365_split' && this.is365SplitNotApplicable(repayment)) {
      this.cancelActiveCellEdit()
      return
    }

    const snapshot = this.snapshotRepayment(repayment)
    this.applyDraftToRepayment(repayment, field, this.editDraftValue)

    if (this.hasRepaymentChanged(snapshot, repayment)) {
      if (this.shouldUseManualSaveMode()) {
        this.markRepaymentPendingManualSave(repayment, snapshot)
      } else {
        await this.persistRepaymentChange(repayment, snapshot, {
          allowPartialNew: true,
        })
      }
    }

    this.cancelActiveCellEdit()
  }

  isTerminalNotApplicable(repayment: Repayment): boolean {
    const isDirectPayment =
      repayment.data_source?.trim().toLowerCase() === 'direct payment'

    return (
      isTerminalNotApplicable(repayment.description, repayment.data_source) ||
      (isDirectPayment && !this.hasTerminalSelection(repayment))
    )
  }

  is365SplitNotApplicable(repayment: Repayment): boolean {
    return repayment.description?.trim().toLowerCase() !== 'daily repayment'
  }

  hasValue(value: unknown): boolean {
    if (value === null || value === undefined) {
      return false
    }

    if (typeof value === 'string') {
      return value.trim().length > 0
    }

    return true
  }

  hasTerminalSelection(repayment: Repayment): boolean {
    return (
      this.hasValue(repayment.terminal_id) ||
      this.hasValue(repayment.terminal_name) ||
      this.hasValue(repayment.terminals?.name)
    )
  }

  hasTerminalAssignment(repayment: Repayment): boolean {
    return (
      this.hasValue(repayment.terminal_id) ||
      this.hasValue(repayment.terminals?.id)
    )
  }

  getTerminalOptionsForRepayment(
    repayment: Repayment,
  ): Array<{ id: string; name: string }> {
    const merchantId = repayment?.merchant_id?.trim()
    const cashAdvanceId = repayment?.cash_advance_id?.trim()
    const scope = cashAdvanceId
      ? `cash-advance:${cashAdvanceId}`
      : merchantId
        ? `merchant:${merchantId}`
        : ''

    if (!scope || this.editableTerminalScope !== scope) {
      return []
    }

    return this.editableTerminalOptions
  }

  getTerminalEmptyMessage(repayment: Repayment): string {
    return repayment?.merchant_id?.trim() || repayment?.cash_advance_id?.trim()
      ? 'No results found'
      : 'Select a merchant or cash advance first'
  }

  onRelationDropdownFilter(
    field: Extract<EditableField, 'merchant_id' | 'terminal_id' | 'cash_advance_id'>,
    term: string,
    repayment?: Repayment,
  ): void {
    const normalizedTerm = term?.trim() ?? ''
    if (!normalizedTerm) {
      if (repayment) {
        this.ensureEditableOptionsLoaded(field, repayment)
        if (field === 'merchant_id') {
          this.syncDropdownOptions()
        }
      } else {
        this.syncDropdownOptions()
      }
      return
    }

    const token = ++this.relationDropdownSearchToken
    const options = { signal: this.lifecycleAbort.signal }
    let search: Promise<Array<{ id?: string; name: string }>>

    if (field === 'merchant_id') {
      search = this.repaymentsService.searchMerchantsByName(normalizedTerm, options)
    } else if (field === 'cash_advance_id') {
      const merchantId = repayment?.merchant_id?.trim()
      search = merchantId
        ? this.repaymentsService.searchMerchantScopedCashAdvancesByName(
            normalizedTerm,
            merchantId,
            options,
          )
        : this.repaymentsService.searchCashAdvancesByName(normalizedTerm, options)
    } else {
      const cashAdvanceId = repayment?.cash_advance_id?.trim()
      const merchantId = repayment?.merchant_id?.trim()
      search = cashAdvanceId
        ? this.repaymentsService.searchCashAdvanceScopedTerminalsByName(
            normalizedTerm,
            cashAdvanceId,
            options,
          )
        : merchantId
          ? this.repaymentsService.searchMerchantScopedTerminalsByName(
              normalizedTerm,
              merchantId,
              options,
            )
          : this.repaymentsService.searchTerminalsByName(normalizedTerm, options)
    }

    void search
      .then((results) => {
        if (this.destroyed || token !== this.relationDropdownSearchToken) {
          return
        }

        const mapped = this.mapToDropdownOptions(results)
        if (field === 'merchant_id') {
          this.merchantOptions = mapped
        } else if (field === 'terminal_id') {
          this.editableTerminalOptions = mapped
        } else {
          this.editableCashAdvanceOptions = mapped
        }
      })
      .catch((error) => this.swallowAbort(error))
  }

  getCashAdvanceOptionsForRepayment(
    repayment: Repayment,
  ): Array<{ id: string; name: string }> {
    const merchantId = repayment?.merchant_id?.trim()
    if (!merchantId || this.editableCashAdvanceMerchantId !== merchantId) {
      return []
    }

    return [...this.editableCashAdvanceOptions].sort((left, right) =>
      right.name.localeCompare(left.name, undefined, {
        numeric: true,
        sensitivity: 'base',
      }),
    )
  }

  getCashAdvanceEmptyMessage(repayment: Repayment): string {
    return repayment?.merchant_id?.trim()
      ? 'No results found'
      : 'Select a merchant first'
  }

  private getCurrentEditableTerminalOptions(): Array<{
    id: string
    name: string
  }> {
    return this.editableTerminalOptions.length > 0
      ? this.editableTerminalOptions
      : this.terminalOptions
  }

  hasCashAdvanceSelection(repayment: Repayment): boolean {
    return (
      this.hasValue(repayment.cash_advance_id) ||
      this.hasValue(repayment.cash_advances?.name)
    )
  }

  hasMerchantSelection(repayment: Repayment): boolean {
    return (
      this.hasValue(repayment.merchant_id) ||
      this.hasValue(repayment.merchants?.name)
    )
  }

  getTerminalDisplay(repayment: Repayment): string {
    if (this.isTerminalNotApplicable(repayment)) {
      return 'N/A'
    }

    return (
      repayment.terminals?.name ||
      repayment.terminal_name ||
      repayment.terminal_id ||
      ''
    )
  }

  getCashAdvanceDisplay(repayment: Repayment): string {
    return repayment.cash_advances?.name || repayment.cash_advance_id || ''
  }

  getMerchantDisplay(repayment: Repayment): string {
    return repayment.merchants?.name || repayment.merchant_id || ''
  }

  get365SplitDisplay(repayment: Repayment): string {
    if (this.is365SplitNotApplicable(repayment)) {
      return 'N/A'
    }
    const percentage = this.get365SplitPercentage(repayment.x365_split)
    if (percentage === null) {
      return ''
    }
    return percentage.toString()
  }

  private get365SplitPercentage(
    value: Repayment['x365_split'],
  ): number | null {
    const numericValue = Number(value)
    if (!Number.isFinite(numericValue) || numericValue === 0) {
      return null
    }
    return Math.abs(numericValue) < 1 ? numericValue * 100 : numericValue
  }

  openCommentsDialog(repayment: Repayment, event?: Event): void {
    event?.stopPropagation()
    if (!this.canEdit || repayment.deleted) {
      return
    }
    this.commentsTargetRepayment = repayment
    this.commentsOriginalValue = this.normalizeCommentsValue(repayment.comments)
    this.commentsDialogValue = this.commentsOriginalValue
    this.commentsDialogVisible = true
  }

  focusCommentsDialogInput(): void {
    setTimeout(() => {
      const input = this.commentsDialogInput?.nativeElement
      if (!input) {
        return
      }
      input.focus()
      const length = input.value?.length ?? 0
      input.setSelectionRange(length, length)
    })
  }

  closeCommentsDialog(): void {
    this.commentsDialogVisible = false
    this.commentsDialogValue = ''
    this.commentsOriginalValue = ''
    this.commentsTargetRepayment = null
  }

  get canSaveComments(): boolean {
    return (
      this.canEdit &&
      !!this.commentsTargetRepayment &&
      this.normalizeCommentsValue(this.commentsDialogValue) !==
        this.commentsOriginalValue
    )
  }

  get isSavingComments(): boolean {
    return !!this.commentsTargetRepayment && this.isRepaymentActionPending(
      this.commentsTargetRepayment,
      'comments',
    )
  }

  async saveComments(): Promise<void> {
    if (
      !this.canEdit ||
      !this.commentsTargetRepayment ||
      !this.canSaveComments ||
      this.destroyed
    ) {
      return
    }

    const repayment = this.commentsTargetRepayment
    const operationKey = this.repaymentActionKey(repayment, 'comments')
    if (!this.operationState.begin(operationKey)) {
      return
    }
    const nextComments = this.normalizeCommentsValue(this.commentsDialogValue)

    if (repayment.isNew) {
      repayment.comments = nextComments
      this.closeCommentsDialog()
      this.operationState.end(operationKey)
      return
    }

    const snapshot = this.snapshotRepayment(repayment)
    const updatedBy = this.authService.getAuditUser()

    repayment.comments = nextComments
    repayment.updated_by = updatedBy
    if (this.shouldUseManualSaveMode()) {
      this.markRepaymentPendingManualSave(repayment, snapshot)
      this.closeCommentsDialog()
      this.operationState.end(operationKey)
      return
    }
    this.repaymentsService.updateRepaymentCache([repayment])

    try {
      const updated = await this.repaymentsService.updateRepayment(
        {
          id: repayment.id,
          comments: nextComments,
          staged: repayment.staged,
        },
        {
          signal: this.lifecycleAbort.signal,
        },
      )
      if (updated) {
        this.applyServerRepayment(repayment, updated)
      }
      this.closeCommentsDialog()
    } catch (error) {
      this.restoreRepayment(repayment, snapshot)
      this.handleOperationError('Could not save comments', error)
    } finally {
      this.operationState.end(operationKey)
    }
  }

  getCommentsDisplay(value: string | undefined | null): string {
    const normalized = this.normalizeCommentsValue(value)
    if (!normalized) {
      return 'Add Comment'
    }

    return normalized
  }

  shouldShowCommentsTooltip(value: string | undefined | null): boolean {
    return (
      this.normalizeCommentsValue(value).length > COMMENTS_TOOLTIP_THRESHOLD
    )
  }

  getCommentsTooltip(value: string | undefined | null): string {
    return this.normalizeCommentsValue(value)
  }

  /**
   * Return tooltip text only if the value exceeds a length threshold
   * (indicates text will likely be truncated in the cell)
   */
  getConditionalTooltip(
    value: string | undefined | null,
    charThreshold: number = 25,
  ): string | null {
    if (!value) return null
    const text = value.toString().trim()
    return text.length > charThreshold ? text : null
  }

  getOverflowTooltip(element: HTMLElement | null | undefined): string | null {
    if (!element) {
      return null
    }

    const text = element.textContent?.trim() ?? ''
    if (!text) {
      return null
    }

    const isOverflowing =
      element.scrollWidth - element.clientWidth > 1 ||
      element.scrollHeight - element.clientHeight > 1

    return isOverflowing ? text : null
  }

  updateOverflowTooltip(element: HTMLElement | null | undefined): void {
    this.activeOverflowTooltip = this.getOverflowTooltip(element)
  }

  clearOverflowTooltip(): void {
    this.activeOverflowTooltip = null
  }

  private snapshotRepayment(repayment: Repayment): Repayment {
    return JSON.parse(JSON.stringify(repayment)) as Repayment
  }

  private restoreRepayment(target: Repayment, snapshot: Repayment): void {
    Object.keys(target ?? {}).forEach((key) => {
      delete (target as any)[key]
    })
    Object.assign(target, snapshot)
    this.repaymentsService.updateRepaymentCache([target])
  }

  private buildRepaymentPayload(repayment: Repayment): Repayment {
    const payload = this.snapshotRepayment(repayment)
    payload.repayment_date = this.normalizeRepaymentDate(payload.repayment_date)
    delete (payload as any).deleted
    delete (payload as any).cash_advances
    delete (payload as any).merchants
    delete (payload as any).terminals
    delete (payload as any).isNew
    return payload
  }

  private applyServerRepayment(
    target: Repayment,
    serverRepayment: Repayment,
  ): void {
    const oldId = String(target.id)
    const normalized = {
      ...serverRepayment,
      repayment_date: this.normalizeRepaymentDate(
        serverRepayment.repayment_date,
      ),
    }
    Object.assign(target, normalized)
    if (
      this.activeEditCell?.repaymentId === oldId &&
      oldId !== String(target.id)
    ) {
      this.activeEditCell = {
        ...this.activeEditCell,
        repaymentId: String(target.id),
      }
    }
    this.repaymentsService.updateRepaymentCache([target])
    this.syncDropdownOptions()
  }

  private normalizeRepaymentDate(
    value: string | Date | undefined | null,
  ): string {
    if (!value) {
      return ''
    }

    if (value instanceof Date) {
      return `${String(value.getDate()).padStart(2, '0')}/${String(
        value.getMonth() + 1,
      ).padStart(2, '0')}/${value.getFullYear()}`
    }

    if (typeof value === 'string') {
      const trimmed = value.trim()
      const yearFirstSlashMatch = trimmed.match(
        /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/,
      )
      if (yearFirstSlashMatch) {
        const [, year, month, day] = yearFirstSlashMatch
        return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`
      }
      const slashParts = trimmed.split('/')
      if (slashParts.length === 3) {
        const [d, m, y] = slashParts
        if (d && m && y) {
          const normalizedYear = y.length === 2 ? `20${y}` : y
          return `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${normalizedYear}`
        }
      }

      const isoParts = trimmed.split('-')
      if (isoParts.length === 3) {
        const [y, m, d] = isoParts
        const parsed = new Date(Number(y), Number(m) - 1, Number(d))
        if (!isNaN(parsed.getTime())) {
          return `${String(parsed.getDate()).padStart(2, '0')}/${String(
            parsed.getMonth() + 1,
          ).padStart(2, '0')}/${parsed.getFullYear()}`
        }
      }

      const parsed = new Date(trimmed)
      if (!isNaN(parsed.getTime())) {
        return `${String(parsed.getDate()).padStart(2, '0')}/${String(
          parsed.getMonth() + 1,
        ).padStart(2, '0')}/${parsed.getFullYear()}`
      }

      return trimmed
    }

    return ''
  }

  private validateNewRepayment(repayment: Repayment): string | undefined {
    const missingFields: string[] = []

    if (!repayment.description?.trim()) {
      missingFields.push('Description')
    }
    if (!repayment.merchant_id?.trim()) {
      missingFields.push('Merchant')
    }
    // Terminal is optional for Direct Payment and non-terminal repayments.
    if (
      isTerminalRequired(repayment.description, repayment.data_source) &&
      !repayment.terminal_id?.trim()
    ) {
      missingFields.push('Terminal')
    }
    if (!repayment.cash_advance_id?.trim()) {
      missingFields.push('Cash Advance')
    }
    if (!repayment.repayment_date?.trim()) {
      missingFields.push('Repayment Date')
    }
    if (!repayment.data_source?.toString().trim()) {
      missingFields.push('Data Source')
    }
    if (missingFields.length > 0) {
      return `Enter values for: ${missingFields.join(', ')}.`
    }

    return this.validateRepaymentAmount(repayment)
  }

  private parsePaymentAmount(
    value: number | string | undefined,
  ): number | null {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null
    }

    if (typeof value !== 'string') {
      return null
    }

    const trimmed = value.trim()
    if (!trimmed) {
      return null
    }

    const normalized = trimmed.replace(/,/g, '')
    if (!/^-?\d+(\.\d+)?$/.test(normalized)) {
      return null
    }

    const parsed = Number(normalized)
    return Number.isFinite(parsed) ? parsed : null
  }

  private validateRepaymentAmount(repayment: Repayment): string | undefined {
    if (
      repayment.payment_amount === undefined ||
      repayment.payment_amount === null ||
      (typeof repayment.payment_amount === 'string' &&
        !repayment.payment_amount.trim())
    ) {
      repayment.payment_amount = ''
      return undefined
    }

    const parsedAmount = this.parsePaymentAmount(repayment.payment_amount)
    if (parsedAmount === null) {
      return 'Amount must contain only digits, with an optional decimal point.'
    }

    repayment.payment_amount = parsedAmount
    return undefined
  }

  private notifyError(summary: string, detail?: string): void {
    this.messageService.add({
      severity: 'error',
      summary,
      detail,
      life: 7000,
    })
  }

  private shouldUseManualSaveMode(): boolean {
    // Keep edits local until the user explicitly commits them. In-progress
    // staging rows retain their existing row-level promotion workflow.
    return !this.isInProgress
  }

  private getRepaymentKey(repayment: Repayment): string {
    return String(repayment.id)
  }

  public isRepaymentPendingManualSave(repayment: Repayment): boolean {
    return this.pendingManualSaveRows.has(this.getRepaymentKey(repayment))
  }

  public shouldShowManualSaveButton(repayment: Repayment): boolean {
    return (
      this.canEdit &&
      this.shouldUseManualSaveMode() &&
      (repayment.isNew || this.isRepaymentPendingManualSave(repayment))
    )
  }

  public shouldShowInProgressSaveButton(repayment: Repayment): boolean {
    return this.canEdit && this.isInProgress
  }

  public shouldShowSaveColumn(): boolean {
    return (
      this.canEdit &&
      (this.isInProgress ||
        (this.repayments ?? []).some((repayment) =>
          this.shouldShowManualSaveButton(repayment),
        ))
    )
  }

  public isRepaymentUnsaved(repayment: Repayment): boolean {
    return !this.isInProgress && this.shouldShowManualSaveButton(repayment)
  }

  private markRepaymentPendingManualSave(
    repayment: Repayment,
    snapshotBeforeChange: Repayment,
  ): void {
    const key = this.getRepaymentKey(repayment)
    if (!this.pendingManualSaveOriginals.has(key)) {
      this.pendingManualSaveOriginals.set(
        key,
        this.snapshotRepayment(snapshotBeforeChange),
      )
    }
    const original = this.pendingManualSaveOriginals.get(key)!
    if (!repayment.isNew && !this.hasRepaymentChanged(original, repayment)) {
      this.clearRepaymentPendingManualSave(key)
      return
    }
    this.pendingManualSaveRows.set(key, this.snapshotRepayment(repayment))
    this.emitPendingChanges()
  }

  private clearRepaymentPendingManualSave(key: string): void {
    this.pendingManualSaveRows.delete(key)
    this.pendingManualSaveOriginals.delete(key)
    this.emitPendingChanges()
  }

  private emitPendingChanges(): void {
    this.pendingChangesChanged.emit(this.hasPendingChanges)
  }

  private withPendingManualSaves(incoming: Repayments): Repayments {
    const merged = incoming.map((repayment) => {
      const pending = this.pendingManualSaveRows.get(
        this.getRepaymentKey(repayment),
      )
      return pending ? this.snapshotRepayment(pending) : repayment
    })

    const existingIds = new Set(
      merged.map((repayment) => this.getRepaymentKey(repayment)),
    )
    const unsynced = Array.from(this.pendingManualSaveRows.values())
      .filter((repayment) => !existingIds.has(this.getRepaymentKey(repayment)))
      .map((repayment) => this.snapshotRepayment(repayment))

    return unsynced.length ? [...unsynced, ...merged] : merged
  }

  private normalizeRepaymentsForView(list: Repayments | undefined): Repayments {
    if (!Array.isArray(list)) {
      return []
    }

    return list.map((repayment) => ({
      ...repayment,
      repayment_date: this.normalizeRepaymentDate(repayment.repayment_date),
    }))
  }

  private normalizeCommentsValue(value: string | undefined | null): string {
    return typeof value === 'string' ? value : ''
  }

  private isNonTerminalDescription(value: string | undefined | null): boolean {
    return isTerminalNotApplicable(value)
  }

  private hasTerminalValue(repayment: Repayment): boolean {
    return Boolean(
      repayment.terminal_id?.trim() ||
        repayment.terminal_name?.trim() ||
        repayment.terminals?.name?.trim(),
    )
  }

  private clearTerminalValue(repayment: Repayment): void {
    repayment.terminal_id = ''
    repayment.terminal_name = ''
    repayment.terminals = undefined
  }

  private async applyDescriptionSelection(
    repayment: Repayment,
    value: string | null,
    clearTerminal: boolean,
  ): Promise<void> {
    if (!this.canEdit) {
      this.cancelActiveCellEdit()
      return
    }
    const snapshot = this.snapshotRepayment(repayment)
    this.editDraftValue = value
    this.applyDraftToRepayment(repayment, 'description', value)

    if (clearTerminal) {
      this.clearTerminalValue(repayment)
    }

    if (this.hasRepaymentChanged(snapshot, repayment)) {
      if (this.shouldUseManualSaveMode()) {
        this.markRepaymentPendingManualSave(repayment, snapshot)
      } else {
        await this.persistRepaymentChange(repayment, snapshot, {
          allowPartialNew: true,
        })
      }
    }

    this.cancelActiveCellEdit()
  }

  private getEditableFieldValue(
    repayment: Repayment,
    field: EditableField,
  ): string | number | null {
    switch (field) {
      case 'description':
        return repayment.description ?? ''
      case 'data_source':
        return repayment.data_source ?? ''
      case 'merchant_id':
        return repayment.merchant_id ?? null
      case 'terminal_id':
        return repayment.terminal_id ?? null
      case 'cash_advance_id':
        return repayment.cash_advance_id ?? null
      case 'repayment_date':
        return repayment.repayment_date ?? ''
      case 'payment_amount':
        return repayment.payment_amount ?? ''
      case 'x365_split':
        return this.get365SplitPercentage(repayment.x365_split) ?? ''
      default:
        return null
    }
  }

  private applyDraftToRepayment(
    repayment: Repayment,
    field: EditableField,
    draftValue: string | number | Date | null,
  ): void {
    switch (field) {
      case 'description':
        repayment.description = typeof draftValue === 'string' ? draftValue : ''
        return
      case 'data_source':
        repayment.data_source =
          typeof draftValue === 'string' ? draftValue : undefined
        return
      case 'merchant_id':
        this.onMerchantSelectionChange(
          repayment,
          typeof draftValue === 'string' ? draftValue : null,
        )
        return
      case 'terminal_id':
        this.onTerminalSelectionChange(
          repayment,
          typeof draftValue === 'string' ? draftValue : null,
        )
        return
      case 'cash_advance_id':
        this.onCashAdvanceSelectionChange(
          repayment,
          typeof draftValue === 'string' ? draftValue : null,
        )
        return
      case 'repayment_date':
        repayment.repayment_date = this.normalizeRepaymentDate(
          draftValue as string | Date | null,
        )
        return
      case 'payment_amount':
        repayment.payment_amount =
          typeof draftValue === 'number' || typeof draftValue === 'string'
            ? draftValue
            : ''
        return
      case 'x365_split':
        repayment.x365_split =
          typeof draftValue === 'number' || typeof draftValue === 'string'
            ? (draftValue as any)
            : ''
        return
    }
  }

  private hasRepaymentChanged(previous: Repayment, next: Repayment): boolean {
    return (
      JSON.stringify(this.buildComparableRepaymentState(previous)) !==
      JSON.stringify(this.buildComparableRepaymentState(next))
    )
  }

  private buildComparableRepaymentState(repayment: Repayment): Repayment {
    const normalized = this.buildRepaymentPayload(repayment)
    const parsedAmount = this.parsePaymentAmount(normalized.payment_amount)

    return {
      ...normalized,
      repayment_date: this.normalizeRepaymentDate(normalized.repayment_date),
      payment_amount:
        parsedAmount !== null ? parsedAmount : normalized.payment_amount,
    }
  }

  private isWithinEditFence(event: MouseEvent): boolean {
    const target = event.target
    if (!(target instanceof Element)) {
      return false
    }

    return (
      this.isWithinActiveEditFence(event) || this.isWithinEditOverlay(target)
    )
  }

  private isWithinActiveEditFence(event: MouseEvent): boolean {
    const tableRoot = this.dataTableChild?.el?.nativeElement as
      | HTMLElement
      | undefined
    const activeCell = tableRoot?.querySelector('.editable-cell-active')

    if (!(activeCell instanceof HTMLElement)) {
      return false
    }

    const rect = activeCell.getBoundingClientRect()
    const left = rect.left - EDIT_FENCE_PADDING_PX
    const right = rect.right + EDIT_FENCE_PADDING_PX
    const top = rect.top - EDIT_FENCE_PADDING_PX
    const bottom = rect.bottom + EDIT_FENCE_PADDING_PX

    return (
      event.clientX >= left &&
      event.clientX <= right &&
      event.clientY >= top &&
      event.clientY <= bottom
    )
  }

  private isWithinEditOverlay(target: Element): boolean {
    return Boolean(
      target.closest(
        '.p-dropdown-panel, .p-select-overlay, .p-datepicker-panel, .p-datepicker, .p-connected-overlay',
      ),
    )
  }

  private async persistRepaymentChange(
    repayment: Repayment,
    snapshot: Repayment,
    options?: { allowPartialNew?: boolean },
  ): Promise<boolean> {
    if (this.destroyed || !this.canEdit) {
      return false
    }

    const formattedDate = this.normalizeRepaymentDate(repayment.repayment_date)
    repayment.repayment_date = formattedDate

    if (repayment.isNew && options?.allowPartialNew) {
      const amountValidationMessage = this.validateRepaymentAmount(repayment)
      if (amountValidationMessage) {
        this.notifyError('Cannot save new repayment', amountValidationMessage)
        this.restoreRepayment(repayment, snapshot)
        return false
      }
    } else {
      const validationMessage = repayment.isNew
        ? this.validateNewRepayment(repayment)
        : this.validateRepaymentAmount(repayment)
      if (validationMessage) {
        this.notifyError(
          repayment.isNew
            ? 'Cannot save new repayment'
            : 'Cannot save repayment',
          validationMessage,
        )
        this.restoreRepayment(repayment, snapshot)
        return false
      }
    }

    const operationKey = this.repaymentActionKey(repayment, 'save')
    if (!this.operationState.begin(operationKey)) {
      return false
    }

    const updatedBy = this.authService.getAuditUser()
    const payload = this.buildRepaymentPayload(repayment)
    payload.repayment_date = formattedDate
    payload.updated_by = updatedBy
    repayment.updated_by = updatedBy

    if (repayment.isNew) {
      delete payload.id
      repayment.isNew = false
      this.repaymentsService.updateRepaymentCache([repayment])
      try {
        const created = await this.repaymentsService.addRepayments([payload], {
          signal: this.lifecycleAbort.signal,
        })
        const createdRepayment =
          Array.isArray(created) && created.length ? created[0] : null
        if (createdRepayment) {
          // Remove the pre-POST temp-ID entry from the service cache. Without this,
          // applyServerRepayment would mutate the object's ID in-place, leaving two
          // cache entries with the same server ID.
          const tempIdx = this.repaymentsService.repayments.indexOf(repayment)
          if (tempIdx >= 0) {
            this.repaymentsService.repayments.splice(tempIdx, 1)
          }
          // Move the server-returned entry to the front of the service cache so that
          // when the view refreshes from the cache the new row appears at the top.
          const serverIdx =
            this.repaymentsService.repayments.indexOf(createdRepayment)
          if (serverIdx > 0) {
            this.repaymentsService.repayments.splice(serverIdx, 1)
            this.repaymentsService.repayments.unshift(createdRepayment)
          }
          this.applyServerRepayment(repayment, createdRepayment)
        }
        await this.reconcileStagedFileStatus(repayment.source_file)
        return true
      } catch (error) {
        this.restoreRepayment(repayment, snapshot)
        this.handleOperationError('Could not save repayment', error)
        return false
      } finally {
        this.operationState.end(operationKey)
      }
    }

    this.repaymentsService.updateRepaymentCache([repayment])
    try {
      const updated = await this.repaymentsService.updateRepayment(payload, {
        signal: this.lifecycleAbort.signal,
      })
      if (updated) {
        this.applyServerRepayment(repayment, updated)
        this.removeRepaymentOutsideEmbeddedCashAdvanceContext(repayment)
      }
      await this.reconcileStagedFileStatus(repayment.source_file)
      return true
    } catch (error) {
      this.restoreRepayment(repayment, snapshot)
      this.handleOperationError('Could not save repayment', error)
      return false
    } finally {
      this.operationState.end(operationKey)
    }
  }

  private async reconcileStagedFileStatus(
    sourceFileId: string | undefined,
  ): Promise<void> {
    if (!this.isInProgress || !sourceFileId || this.destroyed) {
      return
    }

    const file = this.uploadedFileService.files.find(
      (candidate) => candidate.id === sourceFileId,
    )
    if (!file) {
      return
    }

    try {
      const validation = await this.repaymentsService.validateStagedRepayments(
        sourceFileId,
        {
          signal: this.lifecycleAbort.signal,
          requireMerchant: file.source !== FileSource.Custom,
        },
      )
      const nextStatus = validation.valid ? 'UNSAVED' : 'ERROR'
      if (file.status?.toUpperCase() === nextStatus) {
        return
      }
      await this.uploadedFileService.updateFile(
        { ...file, status: nextStatus },
        { signal: this.lifecycleAbort.signal },
      )
    } catch (error) {
      this.swallowAbort(error)
    }
  }

  private async loadMerchantOptions(): Promise<void> {
    try {
      if (!this.repaymentsService.merchants?.length) {
        await this.repaymentsService.loadAllMerchants({
          signal: this.lifecycleAbort.signal,
        })
      }
    } catch (error) {
      this.swallowAbort(error)
    }
  }

  private ensureEditableOptionsLoaded(
    field: EditableField,
    repayment?: Repayment,
  ): void {
    switch (field) {
      case 'merchant_id':
        void this.loadMerchantOptions().finally(() =>
          this.syncDropdownOptions(),
        )
        return
      case 'terminal_id':
        {
          const merchantId = repayment?.merchant_id?.trim()
          const cashAdvanceId = repayment?.cash_advance_id?.trim()

          if (!merchantId && !cashAdvanceId) {
            this.editableTerminalOptions = []
            this.editableTerminalScope = ''
            return
          }

          this.editableTerminalOptions = []
          this.editableTerminalScope = ''
          const loadPromise = cashAdvanceId
            ? this.repaymentsService.loadCashAdvanceScopedTerminals(
                cashAdvanceId,
                { signal: this.lifecycleAbort.signal },
              )
            : this.repaymentsService.loadMerchantScopedTerminals(merchantId!, {
                signal: this.lifecycleAbort.signal,
              })

          void loadPromise
            .then((terminals) => {
              this.editableTerminalOptions =
                this.mapToDropdownOptions(terminals)
              this.editableTerminalScope = cashAdvanceId
                ? `cash-advance:${cashAdvanceId}`
                : `merchant:${merchantId}`

              // Keep a normalized cache locally so row-level filtering has
              // merchant metadata without loading all terminals.
              const existing = Array.isArray(this.repaymentsService.terminals)
                ? this.repaymentsService.terminals
                : []
              const byId = new Map<string, any>()
              existing.forEach((item) => {
                if (item?.id) {
                  byId.set(String(item.id), item)
                }
              })
              terminals.forEach((item) => {
                if (!item?.id) {
                  return
                }
                byId.set(String(item.id), {
                  id: String(item.id),
                  name: String(item.name ?? ''),
                  merchant_id: item.merchantId,
                  cash_advance_id: item.cashAdvanceId,
                })
              })
              this.repaymentsService.terminals = Array.from(byId.values())
            })
            .catch((error) => this.swallowAbort(error))
          return
        }
      case 'cash_advance_id': {
        const merchantId = repayment?.merchant_id?.trim()
        if (!merchantId) {
          this.editableCashAdvanceOptions = []
          this.editableCashAdvanceMerchantId = ''
          return
        }

        this.editableCashAdvanceOptions = []
        this.editableCashAdvanceMerchantId = ''
        void this.repaymentsService
          .loadMerchantScopedCashAdvances(merchantId, {
            signal: this.lifecycleAbort.signal,
          })
          .then((cashAdvances) => {
            this.editableCashAdvanceOptions =
              this.mapToDropdownOptions(cashAdvances)
            this.editableCashAdvanceMerchantId = merchantId

            const existing = Array.isArray(this.repaymentsService.cashAdvances)
              ? this.repaymentsService.cashAdvances
              : []
            const byId = new Map(existing.map((item) => [String(item.id), item]))
            cashAdvances.forEach((item) => {
              if (item?.id) {
                byId.set(String(item.id), {
                  id: String(item.id),
                  name: String(item.name ?? ''),
                  merchant_id: item.merchantId,
                  active_cash_advance: item.activeCashAdvance,
                })
              }
            })
            this.repaymentsService.cashAdvances = Array.from(byId.values())
          })
          .catch((error) => this.swallowAbort(error))
        return
      }
      default:
        return
    }
  }

  canShowQuickFilterAction(name: string | undefined | null): boolean {
    return !this.isEmbedded && !this.sourceFileId && !!name?.trim()
  }

  addRelationToQuickFilter(
    kind: QuickFilterRelationKind,
    relation: { id?: string; name?: string } | undefined,
    event?: Event,
  ): void {
    event?.stopPropagation()
    if (!this.canShowQuickFilterAction(relation?.name)) {
      return
    }

    const normalizedName = relation?.name?.trim()
    if (!normalizedName) {
      return
    }

    const queryParams = { ...this.route.snapshot.queryParams }

    switch (kind) {
      case 'merchant':
        queryParams['merchants(name)'] = this.appendQueryParamValue(
          queryParams['merchants(name)'],
          normalizedName,
        )
        break
      case 'terminal':
        queryParams['terminals(name)'] = this.appendQueryParamValue(
          queryParams['terminals(name)'],
          normalizedName,
        )
        if (relation?.id) {
          queryParams['terminal_id'] = this.appendQueryParamValue(
            queryParams['terminal_id'],
            relation.id,
          )
        }
        break
      case 'cashAdvance':
        queryParams['cash_advances(name)'] = this.appendQueryParamValue(
          queryParams['cash_advances(name)'],
          normalizedName,
        )
        break
    }

    this.router.navigate([''], {
      relativeTo: this.route,
      queryParams,
    })
  }

  private appendQueryParamValue(
    existingValue: string | string[] | null | undefined,
    valueToAdd: string,
  ): string {
    const values = Array.isArray(existingValue)
      ? existingValue.flatMap((value) => String(value).split(','))
      : String(existingValue ?? '')
          .split(',')
          .filter(Boolean)
    const normalizedValues = values.map((value) => value.trim()).filter(Boolean)

    if (!normalizedValues.includes(valueToAdd)) {
      normalizedValues.push(valueToAdd)
    }

    return normalizedValues.join(',')
  }

  private async loadTerminalOptions(): Promise<void> {
    try {
      if (!this.repaymentsService.terminals?.length) {
        await this.repaymentsService.loadAllTerminals({
          signal: this.lifecycleAbort.signal,
        })
      }
    } catch (error) {
      this.swallowAbort(error)
    }
  }

  private async loadCashAdvanceOptions(): Promise<void> {
    try {
      if (!this.repaymentsService.cashAdvances?.length) {
        await this.repaymentsService.loadAllCashAdvances({
          signal: this.lifecycleAbort.signal,
        })
      }
    } catch (error) {
      this.swallowAbort(error)
    }
  }

  private async loadUpdatedByOptions(): Promise<void> {
    try {
      await this.repaymentsService.loadAllUpdatedBy({
        signal: this.lifecycleAbort.signal,
      })
    } catch (error) {
      this.swallowAbort(error)
    }
  }

  private syncDropdownOptions(): void {
    this.merchantOptions = this.mergeRelationOptions(
      this.repaymentsService.merchants,
      (repayment) => repayment.merchants,
    )
    this.terminalOptions = this.mergeRelationOptions(
      this.repaymentsService.terminals,
      (repayment) => repayment.terminals,
    )
    this.cashAdvanceOptions = this.mergeRelationOptions(
      this.repaymentsService.cashAdvances,
      (repayment) => repayment.cash_advances,
    )
    this.updatedByOptions = this.buildUpdatedByOptions()
  }

  private buildUpdatedByOptions(): Array<{ label: string; value: string }> {
    const values = new Set<string>()
    const serviceValues = this.repaymentsService.updatedByValues
    if (Array.isArray(serviceValues)) {
      serviceValues.forEach((value) => {
        if (value) {
          values.add(value)
        }
      })
    }

    if (Array.isArray(this.repayments)) {
      this.repayments.forEach((repayment) => {
        if (repayment.updated_by) {
          values.add(repayment.updated_by)
        }
      })
    }

    const currentUser = this.authService.getAuditUser()
    if (currentUser) {
      values.add(currentUser)
    }

    return Array.from(values)
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({ label: value, value }))
  }

  private mergeRelationOptions(
    serviceItems: Array<{ id?: string; name?: string }> | undefined,
    relationSelector: (
      repayment: Repayment,
    ) => { id?: string; name?: string } | undefined,
  ): Array<{ id: string; name: string }> {
    const base = this.mapToDropdownOptions(serviceItems)
    const seen = new Set(base.map((item) => item.id))
    const repaymentRelations = Array.isArray(this.repayments)
      ? this.repayments
      : []

    repaymentRelations.forEach((repayment) => {
      const relation = relationSelector(repayment)
      if (relation?.id && relation?.name && !seen.has(relation.id)) {
        base.push({ id: relation.id, name: relation.name })
        seen.add(relation.id)
      }
    })

    return base.sort((a, b) => a.name.localeCompare(b.name))
  }

  private mapToDropdownOptions(
    items: Array<{
      id?: string
      name?: string
      customerNumber?: string
      customer_number?: string
      activeCashAdvance?: boolean
      active_cash_advance?: boolean | null
    }> | undefined,
  ): Array<{
    id: string
    name: string
    customerNumber?: string
    activeCashAdvance?: boolean
  }> {
    if (!items) {
      return []
    }

    const seen = new Set<string>()
    return items
      .map((item) => {
        const activeCashAdvance =
          typeof item?.activeCashAdvance === 'boolean'
            ? item.activeCashAdvance
            : typeof item?.active_cash_advance === 'boolean'
              ? item.active_cash_advance
              : undefined
        return {
          id: item?.id ?? '',
          name: item?.name ?? '',
          ...((item?.customerNumber ?? item?.customer_number) === undefined
            ? {}
            : {
                customerNumber:
                  item?.customerNumber ?? item?.customer_number,
              }),
          ...(activeCashAdvance === undefined ? {} : { activeCashAdvance }),
        }
      })
      .filter((item) => {
        if (!item.id || !item.name) {
          return false
        }
        if (seen.has(item.id)) {
          return false
        }
        seen.add(item.id)
        return true
      })
  }

  private getTerminalMerchantId(terminal: any): string | undefined {
    if (typeof terminal?.merchant_id === 'string' && terminal.merchant_id) {
      return terminal.merchant_id
    }

    const relation = Array.isArray(terminal?.cash_advances)
      ? terminal.cash_advances[0]
      : terminal?.cash_advances

    if (typeof relation?.merchant_id === 'string' && relation.merchant_id) {
      return relation.merchant_id
    }

    return undefined
  }

  onMerchantSelectionChange(repayment: Repayment, merchantId: string | null) {
    if (!merchantId) {
      repayment.merchant_id = ''
      repayment.merchants = undefined
      return
    }

    const merchant = this.merchantOptions.find((m) => m.id === merchantId)
    repayment.merchant_id = merchantId
    if (merchant) {
      repayment.merchants = { ...merchant }
    }
    this.clearUnresolvedTerminalName(repayment)
  }

  onTerminalSelectionChange(repayment: Repayment, terminalId: string | null) {
    if (!terminalId) {
      repayment.terminal_id = ''
      repayment.terminal_name = ''
      repayment.terminals = undefined
      return
    }

    const terminal = this.getCurrentEditableTerminalOptions().find(
      (t) => t.id === terminalId,
    )
    repayment.terminal_id = terminalId
    repayment.terminal_name = terminal?.name ?? ''
    if (terminal) {
      repayment.terminals = { ...terminal }
    }

    if (!repayment.merchant_id) {
      const terminalWithMerchant = this.repaymentsService.terminals?.find(
        (item) => item?.id === terminalId,
      )
      const inferredMerchantId =
        this.getTerminalMerchantId(terminalWithMerchant) ?? ''
      if (inferredMerchantId) {
        this.onMerchantSelectionChange(repayment, inferredMerchantId)
      }
    }
  }

  onCashAdvanceSelectionChange(
    repayment: Repayment,
    cashAdvanceId: string | null,
  ) {
    if (!cashAdvanceId) {
      repayment.cash_advance_id = ''
      repayment.cash_advances = undefined
      return
    }

    const cashAdvance = this.getCashAdvanceOptionsForRepayment(repayment).find(
      (item) => item.id === cashAdvanceId,
    )
    repayment.cash_advance_id = cashAdvanceId
    if (cashAdvance) {
      repayment.cash_advances = { ...cashAdvance }
    }
    this.clearUnresolvedTerminalName(repayment)

    if (!repayment.merchant_id) {
      const cashAdvanceWithMerchant = this.repaymentsService.cashAdvances?.find(
        (item) => item?.id === cashAdvanceId,
      )
      const inferredMerchantId =
        typeof cashAdvanceWithMerchant?.merchant_id === 'string'
          ? cashAdvanceWithMerchant.merchant_id
          : ''
      if (inferredMerchantId) {
        this.onMerchantSelectionChange(repayment, inferredMerchantId)
        return
      }

      if (this.isEmbedded && cashAdvanceId) {
        void this.repaymentsService
          .getMerchantIdForCashAdvance(cashAdvanceId, {
            signal: this.lifecycleAbort.signal,
          })
          .then((merchantId) => {
            if (this.destroyed || !merchantId || repayment.merchant_id) {
              return
            }
            this.onMerchantSelectionChange(repayment, merchantId)
            this.repayments = [...this.repayments]
          })
          .catch((error) => this.swallowAbort(error))
      }
    }
  }

  private clearUnresolvedTerminalName(repayment: Repayment): void {
    const hasTerminalRelation = Boolean(
      repayment.terminal_id?.trim() || repayment.terminals?.id?.trim(),
    )

    if (!hasTerminalRelation) {
      repayment.terminal_name = null
    }
  }

  private removeRepaymentOutsideEmbeddedCashAdvanceContext(
    repayment: Repayment,
  ): void {
    const embeddedCashAdvanceId =
      this.embeddedPresetValues.cash_advance_id?.trim()
    const repaymentCashAdvanceId = repayment.cash_advance_id?.trim()

    if (
      !this.isEmbedded ||
      !embeddedCashAdvanceId ||
      embeddedCashAdvanceId === repaymentCashAdvanceId
    ) {
      return
    }

    const repaymentId = String(repayment.id)
    this.repayments = this.repayments.filter(
      (candidate) => String(candidate.id) !== repaymentId,
    )
    this.selectedRepayments = this.selectedRepayments.filter(
      (candidate) => String(candidate.id) !== repaymentId,
    )
    this.totalRepaymentsForQuery = Math.max(
      0,
      (this.totalRepaymentsForQuery ?? this.repayments.length + 1) - 1,
    )
    this.updateHasMoreRepayments()
  }
}
